import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { useCollapseOnScroll } from '../components/useCollapseOnScroll';
import { FilterSheet } from '../components/FilterSheet';

// Public pages live under /club and must read from the anonymous API mirror.
// Derived from the URL rather than passed down as a prop: these files have
// deeply nested components (drilldown sheets, modals, breakdown panels) that
// make their own requests, and threading a prop through every one of them is
// how you end up with `P is not defined` crashing a page on click.
function PUB() {
  return (typeof window !== 'undefined' && window.location.pathname.startsWith('/club'))
    ? '/public' : '';
}


// ─── Theme ──────────────────────────────────────────────────
const NAVY = '#1E3A6E', GOLD = '#F5C518', RED = '#dc2626', GRN = '#16a34a', AMB = '#d97706';

// Format match_date (ISO or Date) as "18 Apr 2024". Handles null/undefined safely.
function fmtDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return String(d).slice(0, 10);
  }
}

const S = {
  page:   { paddingBottom: 80, background: '#f8fafc', minHeight: '100vh' },
  card:   { background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  hdr:    { padding: '9px 14px', background: '#f8fafc', fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.75rem', color: '#475569', letterSpacing: '0.08em', borderBottom: '1px solid #f1f5f9', textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  row:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderBottom: '1px solid #f9fafb' },
  label:  { fontWeight: 600, fontSize: '0.82rem', color: '#64748b' },
  val:    { fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.95rem', color: NAVY },
  grid3:  { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 },
  grid2:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 },
  stat:   (bg = '#f0f9ff') => ({ background: bg, borderRadius: 10, padding: '12px 8px', textAlign: 'center', cursor: 'pointer', position: 'relative' }),
  statV:  { fontFamily: 'Rajdhani,sans-serif', fontWeight: 900, fontSize: '1.3rem', color: NAVY },
  statL:  { fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 },
  tab:    (a) => ({ flex: 1, padding: '9px 2px', border: 'none', borderBottom: a ? `3px solid ${NAVY}` : '3px solid transparent', background: 'white', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.68rem', color: a ? NAVY : '#9ca3af', cursor: 'pointer', textTransform: 'uppercase' }),
  select: { padding: '5px 8px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.78rem', fontWeight: 600, background: 'white', color: NAVY },
  rank:   { width: 22, height: 22, borderRadius: '50%', background: NAVY, color: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.68rem', fontWeight: 800, fontFamily: 'Rajdhani,sans-serif', flexShrink: 0 },
  toggle: (on) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 20, border: `1px solid ${on ? NAVY : '#d1d5db'}`, background: on ? NAVY : 'white', color: on ? 'white' : '#64748b', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'Rajdhani,sans-serif', whiteSpace: 'nowrap' }),
  secLbl: { fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.8rem', color: RED, marginBottom: 8, marginTop: 16, letterSpacing: '0.08em', textTransform: 'uppercase' },
  tapHint: { fontSize: '0.55rem', color: '#cbd5e1', position: 'absolute', bottom: 3, right: 5 },
};

const Loading = () => <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>;


// ═══════════════════════════════════════════════════════════════
// DRILLDOWN SHEET — universal slide-up panel
// ═══════════════════════════════════════════════════════════════
function DrilldownSheet({ memberId, year, stat, filter, onClose }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!stat) return;
    setLoading(true);
    const params = new URLSearchParams({ stat, year: year || 2026 });
    if (filter) params.set('filter', filter);
    api.get(`${PUB()}/stats-v2/drilldown/${memberId}?${params}`)
      .then(r => {
        if (r.data?.error) {
          setData({ title: `Error: ${r.data.error}`, columns: [], rows: [], _error: r.data.error });
        } else {
          setData(r.data);
        }
      })
      .catch(err => {
        const msg = err?.response?.data?.error || err?.message || 'Network error';
        setData({ title: `Failed to load`, columns: [], rows: [], _error: msg });
      })
      .finally(() => setLoading(false));
  }, [memberId, stat, filter, year]);

  if (!stat) return null;

  const fmt = (val) => {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? '✅' : '—';
    if (typeof val === 'string' && val.includes('T')) return fmtDate(val); // date
    return String(val);
  };

  const colKey = (col) => col.toLowerCase().replace(/[^a-z0-9]/g, '_');

  // Map column header to likely data key
  const guessKey = (col, row) => {
    const c = col.toLowerCase();
    const map = {
      'date': 'match_date', 'runs': 'runs', 'balls': 'balls', '4s': 'fours',
      '6s': 'sixes', 'sr': 'strike_rate', 'how out': 'dismissal',
      'dismissed by': 'bowler_name', 'w': 'wickets', 'ov': 'overs', 'o': 'overs',
      'r': 'runs', 'm': 'maidens', 'econ': 'economy', 'wd': 'wides_count',
      'nb': 'noballs_count', 'catches': 'catches', 'stumpings': 'stumpings',
      'run outs': 'run_outs', 'total': 'total', 'result': 'winner_team',
      'points': 'total_pts', 'wickets': 'wickets', 'match result': 'winner_team',
      'economy': 'economy', 'over': 'over_number', 'ball': 'ball_number',
      'boundary': 'is_boundary', 'wicket': 'is_wicket', 'commentary': 'commentary',
      'batsman': 'batsman', 'bowler': 'bowler', 'boundaries': 'boundaries',
      'maiden': 'maidens', '3-wicket': 'hauls', 'average': 'average',
    };
    const key = map[c];
    if (key && row[key] !== undefined) return key;
    // fallback: find first key in row that contains the column hint
    const hint = c.replace(/[^a-z]/g,'');
    return Object.keys(row).find(k => k.toLowerCase().includes(hint)) || Object.keys(row)[0];
  };

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, backdropFilter: 'blur(2px)' }} />
      {/* Sheet */}
      <div style={{ position: 'fixed', bottom: '60px', left: 0, right: 0, background: 'white', borderRadius: '16px 16px 0 0', zIndex: 201, maxHeight: 'calc(85vh - 60px)', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 40px rgba(0,0,0,0.2)' }}>
        {/* Handle + header */}
        <div style={{ padding: '10px 16px 0', flexShrink: 0 }}>
          <div style={{ width: 36, height: 4, background: '#e5e7eb', borderRadius: 2, margin: '0 auto 12px' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div>
              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 900, fontSize: '1.1rem', color: NAVY }}>
                {loading ? '…' : data?.title || stat}
              </div>
              {!loading && data?.rows && (
                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{data.rows.length} record{data.rows.length !== 1 ? 's' : ''}</div>
              )}
            </div>
            <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', borderRadius: 8, padding: '6px 12px', fontSize: '0.8rem', fontWeight: 700, color: '#64748b', cursor: 'pointer' }}>✕ Close</button>
          </div>
        </div>

        {/* Content */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '0 0 80px' }}>
          {loading ? <Loading /> : !data || data.rows.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#9ca3af' }}>
              <div style={{ fontSize: '2rem', marginBottom: 8 }}>🏏</div>
              <div style={{ fontSize: '0.85rem' }}>No records found for this period</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                    {(data.columns || []).map((col, i) => (
                      <th key={i} style={{ padding: '8px 10px', textAlign: i === 0 ? 'left' : 'center', color: '#475569', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', borderBottom: '2px solid #e5e7eb' }}>
                        {col}
                      </th>
                    ))}
                    <th style={{ padding: '8px 10px', borderBottom: '2px solid #e5e7eb' }} />
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, ri) => {
                    const dateStr = fmtDate(row.match_date);
                    const isGood = row.runs >= 30 || row.wickets >= 2 || row.catches >= 2;
                    const rowBg = data.summary?.highlight_above !== undefined
                      ? (parseInt(row.runs || 0) >= data.summary.highlight_above ? '#f0fdf4' : 'white')
                      : 'white';
                    return (
                      <tr key={ri} style={{ borderBottom: '1px solid #f1f5f9', background: rowBg }}>
                        {(data.columns || []).map((col, ci) => {
                          const key = guessKey(col, row);
                          const raw = row[key];
                          let display = fmt(raw);
                          // Color-code runs
                          let cellColor = '#334155';
                          if (col === 'Date') { display = dateStr; cellColor = '#94a3b8'; }
                          if (col === 'Runs' || col === 'R') {
                            const v = parseInt(raw || 0);
                            cellColor = v >= 50 ? GRN : v >= 20 ? AMB : v === 0 ? RED : NAVY;
                          }
                          if ((col === 'W' || col === 'Wickets') && parseInt(raw) >= 3) cellColor = GRN;
                          if (col === 'Boundary' || col === 'Wicket') display = raw ? (col === 'Boundary' ? '🏏' : '🎯') : '';
                          if (col === 'How Out') { display = (raw || '').replace(/_/g, ' '); cellColor = '#dc2626'; }
                          if (col === 'Result') cellColor = (raw || '').includes('UCC') ? GRN : AMB;
                          return (
                            <td key={ci} style={{ padding: '8px 10px', textAlign: ci === 0 ? 'left' : 'center', fontFamily: ci === 0 ? 'inherit' : 'Rajdhani,sans-serif', fontWeight: ci === 0 ? 500 : 700, color: cellColor, whiteSpace: 'nowrap' }}>
                              {display}
                            </td>
                          );
                        })}
                        {/* Match detail link */}
                        <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                          {(row.match_id || row.match_id) && (
                            <Link to={`/match/${row.match_id}`} onClick={onClose}
                              style={{ fontSize: '0.68rem', color: NAVY, textDecoration: 'none', background: '#f0f9ff', padding: '2px 7px', borderRadius: 6, fontWeight: 700 }}>
                              🔍
                            </Link>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}


// ── Tappable Stat Box ────────────────────────────────────────
function StatTap({ v, l, bg, sub, stat, filter, onDrill }) {
  const tappable = !!stat && !!onDrill && (v !== '—' && v !== 0 && v !== '0' && v !== null);
  return (
    <div style={{ ...S.stat(bg), border: tappable ? '1px solid #dbeafe' : 'none' }}
      onClick={() => tappable && onDrill(stat, filter)}>
      <div style={{ ...S.statV, color: tappable ? NAVY : '#94a3b8' }}>{v ?? '—'}</div>
      {sub && <div style={{ fontSize: '0.62rem', color: '#94a3b8', fontFamily: 'Rajdhani,sans-serif' }}>{sub}</div>}
      <div style={S.statL}>{l}</div>
      {tappable && <span style={S.tapHint}>tap ↗</span>}
    </div>
  );
}

// ── Tappable Row ─────────────────────────────────────────────
function RowTap({ label, value, emoji, stat, filter, onDrill, sub }) {
  const tappable = !!stat && !!onDrill && value !== '—' && value !== null && value !== 0 && value !== '0';
  return (
    <div style={{ ...S.row, cursor: tappable ? 'pointer' : 'default', background: tappable ? 'white' : 'white' }}
      onClick={() => tappable && onDrill(stat, filter)}>
      <span style={S.label}>{emoji && <span style={{ marginRight: 4 }}>{emoji}</span>}{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ textAlign: 'right' }}>
          <span style={{ ...S.val, color: tappable ? NAVY : '#94a3b8' }}>{value ?? '—'}</span>
          {sub && <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{sub}</div>}
        </div>
        {tappable && <span style={{ fontSize: '0.65rem', color: '#cbd5e1', fontWeight: 700 }}>↗</span>}
      </div>
    </div>
  );
}

// ── HiPair — two-up highlight block (e.g. Strongest | Weakest) ──
// Session 18. Pure display, no drill. Greys out a side with no data.
function HiPair({ leftLabel, leftName, leftSub, rightLabel, rightName, rightSub }) {
  const cell = (label, name, sub, accent) => (
    <div style={{ flex: 1, padding: '10px 12px', textAlign: 'center' }}>
      <div style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{label}</div>
      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.95rem', color: name ? accent : '#cbd5e1' }}>{name || '—'}</div>
      {sub && name && <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: 1 }}>{sub}</div>}
    </div>
  );
  return (
    <div style={{ display: 'flex', borderBottom: '1px solid #f9fafb' }}>
      {cell(leftLabel, leftName, leftSub, GRN)}
      <div style={{ width: 1, background: '#f1f5f9' }} />
      {cell(rightLabel, rightName, rightSub, RED)}
    </div>
  );
}

// ── Controls (year chips + active toggle) — Session 16 multi-year support ───
// `year` is still passed as the backend query string: 'all' | '2026' | '2024,2025,2026'
// Internally bridges to YearChips object-state shape.

// Format the scalar `year` query string as a human-readable label.
// 'all' | 'All Time' | '2026' | '2024, 2025, 2026' | '4 Seasons'
function yearLabel(year) {
  if (!year || year === 'all') return 'All Time';
  if (!String(year).includes(',')) return String(year);
  const vals = String(year).split(',').map(s => s.trim()).filter(Boolean);
  if (vals.length <= 3) return vals.join(', ');
  return `${vals.length} Seasons`;
}
const Controls = ({ year, setYear, seasons, includeInactive, setIncludeInactive, showAll = true }) => {
  const availableYears = seasons || [2026,2025,2024,2023,2022,2021,2020,2019,2018,2017,2016,2015,2014,2013];
  // Bridge scalar ↔ object state
  let yearsState;
  if (!year || year === 'all') yearsState = { mode: 'all' };
  else if (String(year).includes(',')) {
    const vals = String(year).split(',').map(s => parseInt(s,10)).filter(Boolean);
    yearsState = vals.length === 1 ? { mode: 'single', value: vals[0] } : { mode: 'multi', values: vals };
  }
  else yearsState = { mode: 'single', value: parseInt(year, 10) };

  function handleChange(next) {
    if (!next || next.mode === 'all') setYear(showAll ? 'all' : String(availableYears[0] || 2026));
    else if (next.mode === 'single') setYear(String(next.value));
    else setYear((next.values || []).join(','));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Row 1 — year chips */}
      <YearChipsInline years={yearsState} availableYears={availableYears}
        showAll={showAll} onChange={handleChange} />
      {/* Row 2 — Active Only toggle (right-aligned) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button style={S.toggle(includeInactive)} onClick={() => setIncludeInactive(v => !v)}>
          {includeInactive ? '👥 All Members' : '⭐ Active Only'}
        </button>
      </div>
    </div>
  );
};

// Inline chip row component — dark-header-friendly (no external import to keep StatsV2Pages self-contained)
const YearChipsInline = ({ years, availableYears, showAll = true, onChange }) => {
  const mode = years?.mode || 'all';
  const selected = mode === 'single' ? [years.value] : mode === 'multi' ? (years.values || []) : [];
  const [moreOpen, setMoreOpen] = React.useState(false);

  // Show only 4 most recent years inline + "More ▾" button.
  // This keeps the row tidy on mobile (no horizontal scroll needed).
  const RECENT_COUNT = 4;
  const recentYears = availableYears.slice(0, RECENT_COUNT);
  const earlierYears = availableYears.slice(RECENT_COUNT);

  // If a selected year is NOT in the recent list, also surface it inline
  // so users always see their selection (e.g. 2018 picked → it appears even though it's not "recent")
  const extraSelected = selected.filter(y => !recentYears.includes(y));
  const visibleYears = [...recentYears, ...extraSelected].sort((a,b)=>b-a);

  function onTap(y) {
    if (mode === 'all') return onChange({ mode: 'single', value: y });
    if (mode === 'single') {
      if (years.value === y) return;
      return onChange({ mode: 'multi', values: [years.value, y].sort((a,b)=>b-a) });
    }
    const cur = years.values || [];
    if (cur.includes(y)) {
      const next = cur.filter(v => v !== y);
      if (next.length === 0) return onChange({ mode: 'all' });
      if (next.length === 1) return onChange({ mode: 'single', value: next[0] });
      return onChange({ mode: 'multi', values: next.sort((a,b)=>b-a) });
    }
    onChange({ mode: 'multi', values: [...cur, y].sort((a,b)=>b-a) });
  }

  const pill = (active) => ({
    padding: '6px 12px', borderRadius: 18, border: '1px solid',
    borderColor: active ? '#F5C518' : 'rgba(255,255,255,0.25)',
    background: active ? '#F5C518' : 'rgba(255,255,255,0.08)',
    color: active ? '#1a1a1a' : 'rgba(255,255,255,0.9)',
    fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.78rem',
    cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.15s',
  });

  return (
    <>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {showAll && <button onClick={() => onChange({ mode: 'all' })} style={pill(mode === 'all')}>All Time</button>}
        {visibleYears.map(y => (
          <button key={y} onClick={() => onTap(y)} style={pill(selected.includes(y))}>{y}</button>
        ))}
        {earlierYears.length > 0 && (
          <button onClick={() => setMoreOpen(true)} style={{
            ...pill(false),
            borderStyle: 'dashed',
            color: '#F5C518',
          }}>More ▾</button>
        )}
      </div>

      {/* More-years modal */}
      {moreOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 300,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }} onClick={(e) => { if (e.target === e.currentTarget) setMoreOpen(false); }}>
          <div style={{
            background: 'white', borderRadius: '18px 18px 0 0', width: '100%',
            maxWidth: 520, maxHeight: '85vh', overflowY: 'auto',
            padding: '18px 18px calc(18px + env(safe-area-inset-bottom, 0px))',
          }}>
            <div style={{ width: 40, height: 4, background: '#e5e7eb', borderRadius: 2, margin: '0 auto 14px' }} />
            <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1rem', color: '#1E3A6E', marginBottom: 4 }}>
              Select Seasons
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 14 }}>
              Tap any year to add or remove. Multiple years combine their stats.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {availableYears.map(y => {
                const active = selected.includes(y);
                return (
                  <button key={y} onClick={() => onTap(y)}
                    style={{
                      padding: '10px 0', borderRadius: 10, border: '2px solid',
                      borderColor: active ? '#1E3A6E' : '#e5e7eb',
                      background: active ? '#1E3A6E' : 'white',
                      color: active ? 'white' : '#374151',
                      fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.88rem',
                      cursor: 'pointer',
                    }}>{y}</button>
                );
              })}
            </div>
            <button onClick={() => setMoreOpen(false)} style={{
              width: '100%', marginTop: 16, padding: '11px', borderRadius: 10,
              border: 'none', background: '#1E3A6E', color: 'white',
              fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer',
            }}>Done</button>
          </div>
        </div>
      )}
    </>
  );
};


// ── Visualization Components ─────────────────────────────────

const WagonWheel = ({ zones, onDrill }) => {
  if (!zones || zones.length === 0) return (
    <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af', fontSize: '0.8rem' }}>No wagon wheel data</div>
  );
  const W = 280, CX = 140, CY = 148, R = 108, PR = 18;
  const zoneAngles = {
    'Fine Leg':   { from: 145, to: 170, color: '#3b82f6' },
    'Square Leg': { from: 110, to: 145, color: '#8b5cf6' },
    'Mid-Wicket': { from: 70, to: 110, color: '#ec4899' },
    'Mid-On':     { from: 40, to: 70,  color: '#f97316' },
    'Straight':   { from: -10, to: 40, color: '#22c55e' },
    'Mid-Off':    { from: -40, to: -10, color: '#eab308' },
    'Cover':      { from: -80, to: -40, color: '#14b8a6' },
    'Point':      { from: -110, to: -80, color: '#f43f5e' },
    'Third Man':  { from: -170, to: -145, color: '#a855f7' },
    'Long-Off':   { from: -40, to: -10, color: '#06b6d4' },
    'Long-On':    { from: 40, to: 70,  color: '#84cc16' },
    'Cow Corner': { from: 70, to: 110, color: '#ef4444' },
  };
  const toRad = d => d * Math.PI / 180;
  const arcPt = (a, r) => ({ x: CX + r * Math.sin(toRad(a)), y: CY - r * Math.cos(toRad(a)) });
  const total = zones.reduce((s, z) => s + parseInt(z.runs || 0), 0);
  const arcs = zones.map(z => {
    const za = zoneAngles[z.zone]; if (!za) return null;
    const runs = parseInt(z.runs || 0);
    const thick = Math.max(5, Math.min(30, (runs / Math.max(total, 1)) * 90 + 5));
    const mid = (za.from + za.to) / 2;
    const inner = R * 0.28, outer = R * (0.28 + thick / 100);
    const p1 = arcPt(za.from, inner), p2 = arcPt(za.to, inner);
    const p3 = arcPt(za.to, outer), p4 = arcPt(za.from, outer);
    return { ...z, za, runs, mid, p1, p2, p3, p4, color: za.color };
  }).filter(Boolean);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <svg width={W} height={W} viewBox={`0 0 ${W} ${W}`} style={{ maxWidth: 280 }}>
        <ellipse cx={CX} cy={CY} rx={R} ry={R*0.92} fill="#22c55e18" stroke="#16a34a33" strokeWidth="1.5" />
        <ellipse cx={CX} cy={CY} rx={R*0.54} ry={R*0.5} fill="none" stroke="#16a34a33" strokeWidth="1" strokeDasharray="4,3" />
        <rect x={CX-8} y={CY-PR} width={16} height={PR*2} fill="#d4a55488" rx="2" />
        <line x1={CX-12} y1={CY-PR} x2={CX+12} y2={CY-PR} stroke="#92400e" strokeWidth="1.5" />
        <line x1={CX-12} y1={CY+PR} x2={CX+12} y2={CY+PR} stroke="#92400e" strokeWidth="1.5" />
        {arcs.map((a, i) => (
          <path key={i} onClick={() => onDrill && onDrill('wagon_zone', a.zone)}
            style={{ cursor: onDrill ? 'pointer' : 'default' }}
            d={`M ${a.p1.x} ${a.p1.y} A ${R*0.28} ${R*0.28} 0 0 1 ${a.p2.x} ${a.p2.y} L ${a.p3.x} ${a.p3.y} A ${R*0.28} ${R*0.28} 0 0 0 ${a.p4.x} ${a.p4.y} Z`}
            fill={a.color} fillOpacity="0.78" stroke={a.color} strokeWidth="0.5"
          />
        ))}
        <circle cx={CX} cy={CY+PR/2} r="4" fill={NAVY} />
      </svg>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', padding: '4px 14px', justifyContent: 'center' }}>
        {arcs.sort((a,b)=>b.runs-a.runs).map((a,i) => (
          <div key={i} onClick={() => onDrill && onDrill('wagon_zone', a.zone)}
            style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: '0.65rem', cursor: onDrill?'pointer':'default' }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: a.color }} />
            <span style={{ color: '#64748b' }}>{a.zone}</span>
            <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, color: NAVY }}>{a.runs}r ↗</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const RadarChart = ({ players, size = 220 }) => {
  if (!players || players.length === 0) return null;
  const dims = Object.keys(players[0]?.dimensions || {});
  const N = dims.length; if (N < 3) return null;
  const CX = size/2, CY = size/2, R = size*0.38;
  const angle = i => (i/N)*2*Math.PI - Math.PI/2;
  const pt = (i, r) => ({ x: CX + r*Math.cos(angle(i)), y: CY + r*Math.sin(angle(i)) });
  const colors = [NAVY, RED, GRN];
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: 'visible' }}>
      {[20,40,60,80,100].map(pct => (
        <polygon key={pct} points={dims.map((_,i)=>{ const p=pt(i,R*pct/100); return `${p.x},${p.y}`; }).join(' ')} fill="none" stroke="#e5e7eb" strokeWidth="0.8" />
      ))}
      {dims.map((_,i) => { const p=pt(i,R); return <line key={i} x1={CX} y1={CY} x2={p.x} y2={p.y} stroke="#e5e7eb" strokeWidth="0.8" />; })}
      {players.map((pl,pi) => (
        <polygon key={pi}
          points={dims.map((d,i)=>{ const s=pl.dimensions[d]?.score||0; const p=pt(i,R*s/100); return `${p.x},${p.y}`; }).join(' ')}
          fill={colors[pi%colors.length]} fillOpacity="0.15" stroke={colors[pi%colors.length]} strokeWidth="2" />
      ))}
      {dims.map((d,i) => { const p=pt(i,R+20); const dim=players[0]?.dimensions[d]; return (
        <text key={i} x={p.x} y={p.y} textAnchor="middle" dominantBaseline="middle" fontSize="8" fontFamily="Rajdhani,sans-serif" fontWeight="700" fill="#475569">{dim?.label||d}</text>
      ); })}
      {players.map((pl,pi) => dims.map((d,i) => { const s=pl.dimensions[d]?.score||0; const p=pt(i,R*s/100); return <circle key={`${pi}-${i}`} cx={p.x} cy={p.y} r="3" fill={colors[pi%colors.length]} stroke="white" strokeWidth="1" />; }))}
    </svg>
  );
};

const OverHeatMap = ({ data, label = 'Batting', maxOvers = 20 }) => {
  if (!data || data.length === 0) return <div style={{ padding: 14, color: '#9ca3af', textAlign: 'center', fontSize: '0.8rem' }}>No data</div>;
  const grid = {};
  data.forEach(c => { grid[`${c.over_number}-${c.ball_pos}`] = c; });
  const maxR = Math.max(...data.map(c => parseFloat(c.avg_runs||0)), 0.01);
  const colCell = avg => { if (!avg||avg===0) return '#f1f5f9'; const p=avg/maxR; return p>0.75?'#dc2626':p>0.5?'#f97316':p>0.25?'#eab308':'#22c55e'; };
  const overs = Array.from({length:maxOvers},(_,i)=>i+1), balls=[1,2,3,4,5,6];
  return (
    <div style={{ padding: '10px 14px', overflowX: 'auto' }}>
      <div style={{ fontSize: '0.63rem', color: '#94a3b8', marginBottom: 6, fontWeight: 600 }}>{label} — avg runs per ball (tap zone for details)</div>
      <div style={{ display: 'flex', gap: 1 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, marginRight: 4 }}>
          <div style={{ height: 14 }} />
          {balls.map(b=><div key={b} style={{ height: 14, display:'flex', alignItems:'center', fontSize:'0.55rem', color:'#94a3b8', fontFamily:'Rajdhani,sans-serif', fontWeight:700 }}>b{b}</div>)}
        </div>
        {overs.map(ov=>(
          <div key={ov} style={{ display:'flex',flexDirection:'column',gap:1 }}>
            <div style={{ height:14, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.52rem', color:'#94a3b8' }}>{ov}</div>
            {balls.map(b=>{
              const cell=grid[`${ov}-${b}`], avg=cell?parseFloat(cell.avg_runs):0;
              return <div key={b} title={cell?`Ov${ov} B${b}: avg ${avg.toFixed(2)}`:`Ov${ov} B${b}: no data`} style={{ width:14, height:14, background:colCell(avg), borderRadius:2, cursor:'default' }} />;
            })}
          </div>
        ))}
      </div>
      <div style={{ display:'flex', gap:8, marginTop:8, fontSize:'0.6rem', color:'#94a3b8' }}>
        {[['#22c55e','Low'],['#eab308','Med'],['#f97316','High'],['#dc2626','Max']].map(([c,l])=>(
          <span key={l}><span style={{ background:c, padding:'1px 5px', borderRadius:3 }}>&nbsp;</span> {l}</span>
        ))}
      </div>
    </div>
  );
};

const WormChart = ({ worm = {}, wicketsPerOver = [] }) => {
  const d1=worm.inn1||[], d2=worm.inn2||[];
  if (d1.length===0&&d2.length===0) return null;
  const W=340,H=180,PL=34,PR=14,PT=10,PB=26,gW=W-PL-PR,gH=H-PT-PB;
  const maxO=Math.max(d1.length,d2.length,1), maxR=Math.max(...d1.map(d=>d.total||0),...d2.map(d=>d.total||0),10);
  const scX=i=>PL+(i/maxO)*gW, scY=r=>PT+gH-(r/maxR)*gH;
  const lp=arr=>arr.length===0?'':  `M ${scX(0)},${scY(0)} `+arr.map((d,i)=>`L ${scX(i+1)},${scY(d.total)}`).join(' ');
  const yT=[0,Math.round(maxR*.25),Math.round(maxR*.5),Math.round(maxR*.75),maxR];
  const xT=Array.from({length:Math.floor(maxO/5)+1},(_,i)=>i*5).filter(v=>v<=maxO);
  const w1=wicketsPerOver.filter(w=>parseInt(w.innings)===1), w2=wicketsPerOver.filter(w=>parseInt(w.innings)===2);
  const wY=(ov,arr)=>{const p=arr.find(d=>parseInt(d.over)===parseInt(ov))||arr[parseInt(ov)-1]; return p?scY(p.total):scY(0);};
  return (
    <div style={{ padding: '8px 14px', overflowX: 'auto' }}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ minWidth: 280 }}>
        {yT.map(t=><g key={t}><line x1={PL} y1={scY(t)} x2={W-PR} y2={scY(t)} stroke="#f1f5f9" strokeWidth="1" /><text x={PL-4} y={scY(t)+3} textAnchor="end" fontSize="8" fill="#94a3b8" fontFamily="Rajdhani,sans-serif">{t}</text></g>)}
        {xT.map(t=><g key={t}><line x1={scX(t)} y1={PT} x2={scX(t)} y2={H-PB} stroke="#f1f5f9" strokeWidth="1" /><text x={scX(t)} y={H-PB+12} textAnchor="middle" fontSize="8" fill="#94a3b8" fontFamily="Rajdhani,sans-serif">{t}</text></g>)}
        <line x1={PL} y1={PT} x2={PL} y2={H-PB} stroke="#cbd5e1" strokeWidth="1" />
        <line x1={PL} y1={H-PB} x2={W-PR} y2={H-PB} stroke="#cbd5e1" strokeWidth="1" />
        {d1.length>0&&<path d={lp(d1)+` L ${scX(d1.length)},${H-PB} L ${scX(0)},${H-PB} Z`} fill={NAVY} fillOpacity="0.07" />}
        {d2.length>0&&<path d={lp(d2)+` L ${scX(d2.length)},${H-PB} L ${scX(0)},${H-PB} Z`} fill={RED} fillOpacity="0.06" />}
        {d1.length>0&&<path d={lp(d1)} fill="none" stroke={NAVY} strokeWidth="2.5" strokeLinecap="round" />}
        {d2.length>0&&<path d={lp(d2)} fill="none" stroke={RED} strokeWidth="2.5" strokeLinecap="round" strokeDasharray="6,3" />}
        {w1.map((w,i)=><g key={i}><line x1={scX(w.over_number)} y1={PT} x2={scX(w.over_number)} y2={H-PB} stroke={NAVY} strokeWidth="1" strokeDasharray="3,2" strokeOpacity="0.5" /><text x={scX(w.over_number)} y={wY(w.over_number,d1)-5} textAnchor="middle" fontSize="9" fill={NAVY}>🎯</text></g>)}
        {w2.map((w,i)=><g key={i}><line x1={scX(w.over_number)} y1={PT} x2={scX(w.over_number)} y2={H-PB} stroke={RED} strokeWidth="1" strokeDasharray="3,2" strokeOpacity="0.5" /><text x={scX(w.over_number)} y={wY(w.over_number,d2)-5} textAnchor="middle" fontSize="9" fill={RED}>🎯</text></g>)}
        {d1.length>0&&<text x={scX(d1.length)+2} y={scY(d1[d1.length-1].total)+3} fontSize="9" fontFamily="Rajdhani,sans-serif" fontWeight="700" fill={NAVY}>{d1[d1.length-1].total}</text>}
        {d2.length>0&&<text x={scX(d2.length)+2} y={scY(d2[d2.length-1].total)+3} fontSize="9" fontFamily="Rajdhani,sans-serif" fontWeight="700" fill={RED}>{d2[d2.length-1].total}</text>}
      </svg>
      <div style={{ display:'flex', gap:16, fontSize:'0.7rem', color:'#64748b', justifyContent:'center', marginTop:4 }}>
        {d1.length>0&&<span><span style={{color:NAVY,fontWeight:800}}>—</span> Innings 1 · {d1[d1.length-1]?.total||0}</span>}
        {d2.length>0&&<span><span style={{color:RED,fontWeight:800}}>- -</span> Innings 2 · {d2[d2.length-1]?.total||0}</span>}
      </div>
    </div>
  );
};

const BarChart = ({ data, xKey, yKey, color = NAVY, height = 90 }) => {
  if (!data||data.length===0) return null;
  const max=Math.max(...data.map(d=>parseFloat(d[yKey])||0),0.01);
  const W=320,H=height,PB=18,PL=24,PT=6;
  const bW=Math.max(4,(W-PL)/data.length-2);
  const scY=v=>PT+(H-PB-PT)*(1-v/max);
  return (
    <div style={{ padding:'6px 14px', overflowX:'auto' }}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ minWidth:Math.max(200,data.length*18) }}>
        <line x1={PL} y1={PT} x2={PL} y2={H-PB} stroke="#e5e7eb" strokeWidth="1" />
        {[0,.5,1].map(p=>{const v=Math.round(max*p); return <g key={p}><line x1={PL} y1={scY(v)} x2={W} y2={scY(v)} stroke="#f1f5f9" strokeWidth="1" /><text x={PL-3} y={scY(v)+3} textAnchor="end" fontSize="8" fill="#94a3b8" fontFamily="Rajdhani,sans-serif">{v}</text></g>;})}
        {data.map((d,i)=>{
          const v=parseFloat(d[yKey])||0, x=PL+4+i*((W-PL-4)/data.length), y=scY(v), bH=(H-PB)-y;
          return <g key={i}><rect x={x} y={y} width={bW} height={Math.max(bH,0)} fill={color} rx="2" fillOpacity="0.8" /><text x={x+bW/2} y={H-PB+11} textAnchor="middle" fontSize="6.5" fill="#94a3b8" fontFamily="Rajdhani,sans-serif">{d[xKey]}</text></g>;
        })}
      </svg>
    </div>
  );
};

const PhaseBar = ({ label, runs, balls, color = NAVY, statKey, onDrill }) => {
  const sr = balls>0?Math.round(runs/balls*100):0;
  const pct = Math.min(sr/200*100,100);
  const col = sr>=150?RED:sr>=100?AMB:GRN;
  return (
    <div style={{ marginBottom:12, padding:'0 14px', cursor: onDrill?'pointer':'default' }}
      onClick={() => onDrill && onDrill(statKey)}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
        <span style={{ fontSize:'0.75rem', fontWeight:600, color:'#64748b' }}>{label}</span>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1rem', color:col }}>{sr} SR</span>
          <span style={{ fontSize:'0.65rem', color:'#94a3b8' }}>{runs}({balls}b)</span>
          {onDrill&&<span style={{ fontSize:'0.65rem', color:'#cbd5e1' }}>↗</span>}
        </div>
      </div>
      <div style={{ height:8, background:'#f1f5f9', borderRadius:4, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:4, transition:'width 0.5s' }} />
      </div>
    </div>
  );
};

const RollingFormChart = ({ data }) => {
  if (!data||data.length<2) return null;
  const rolling = data.map((d,i)=>{
    const sl=data.slice(Math.max(0,i-4),i+1);
    return {...d, ra:Math.round(sl.reduce((s,r)=>s+parseInt(r.runs||0),0)/sl.length*10)/10};
  });
  const W=340,H=110,PL=28,PB=20,PT=6,PR=8,gW=W-PL-PR,gH=H-PT-PB;
  const maxR=Math.max(...rolling.map(r=>parseInt(r.runs||0)),...rolling.map(r=>r.ra),10);
  const sx=i=>PL+(i/(rolling.length-1))*gW, sy=v=>PT+gH-(v/maxR)*gH;
  const lp='M '+rolling.map((r,i)=>`${sx(i)},${sy(r.ra)}`).join(' L ');
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ minWidth:240 }}>
      {[0,Math.round(maxR/2),maxR].map(t=><g key={t}><line x1={PL} y1={sy(t)} x2={W-PR} y2={sy(t)} stroke="#f1f5f9" strokeWidth="1" /><text x={PL-3} y={sy(t)+3} textAnchor="end" fontSize="8" fill="#94a3b8" fontFamily="Rajdhani,sans-serif">{t}</text></g>)}
      {rolling.map((r,i)=>{
        const v=parseInt(r.runs||0),x=sx(i),y=sy(v),bH=(H-PB)-y;
        return <rect key={i} x={x-3} y={y} width={6} height={Math.max(bH,0)} fill={v>=50?GRN:v>=20?AMB:v===0?RED:'#94a3b8'} fillOpacity="0.45" rx="1" />;
      })}
      <path d={lp+` L ${sx(rolling.length-1)},${H-PB} L ${sx(0)},${H-PB} Z`} fill={NAVY} fillOpacity="0.06" />
      <path d={lp} fill="none" stroke={NAVY} strokeWidth="2" strokeLinecap="round" />
      <line x1={PL} y1={PT} x2={PL} y2={H-PB} stroke="#e5e7eb" strokeWidth="1" />
      <line x1={PL} y1={H-PB} x2={W-PR} y2={H-PB} stroke="#e5e7eb" strokeWidth="1" />
    </svg>
  );
};

const StreakBadge = ({ label, current, longest, emoji, good, onDrill, stat }) => (
  <div style={{ background:'#f8fafc', borderRadius:10, padding:'10px 12px', border:'1px solid #e5e7eb', cursor: onDrill?'pointer':'default' }}
    onClick={() => onDrill && stat && onDrill(stat)}>
    <div style={{ fontSize:'0.6rem', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4, display:'flex', justifyContent:'space-between' }}>
      <span>{emoji} {label}</span>
      {onDrill && <span style={{ color:'#cbd5e1' }}>↗</span>}
    </div>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end' }}>
      <div>
        <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.6rem', color: current>0&&good?GRN:current>0&&!good?RED:NAVY }}>{current}</div>
        <div style={{ fontSize:'0.58rem', color:'#94a3b8' }}>Current</div>
      </div>
      <div style={{ textAlign:'right' }}>
        <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1rem', color:'#334155' }}>{longest}</div>
        <div style={{ fontSize:'0.58rem', color:'#94a3b8' }}>Best ever</div>
      </div>
    </div>
  </div>
);

const DotRing = ({ pct, label, color = RED, size = 68, stat, onDrill }) => {
  const r=size*0.38, cx=size/2, cy=size/2, circ=2*Math.PI*r, dash=(pct/100)*circ;
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', cursor: onDrill?'pointer':'default' }}
      onClick={() => onDrill && stat && onDrill(stat)}>
      <svg width={size} height={size}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f1f5f9" strokeWidth={size*0.1} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={size*0.1}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{ transform:`rotate(-90deg)`, transformOrigin:`${cx}px ${cy}px` }} />
        <text x={cx} y={cy+3} textAnchor="middle" fontSize={size*0.17} fontFamily="Rajdhani,sans-serif" fontWeight="900" fill={NAVY}>{pct}%</text>
      </svg>
      <div style={{ fontSize:'0.56rem', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em', textAlign:'center' }}>
        {label}{onDrill&&<span style={{ color:'#cbd5e1', marginLeft:3 }}>↗</span>}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// PLAYER PROFILE PAGE
// ═══════════════════════════════════════════════════════════════
export function PlayerProfilePage({ publicMode = false }) {
  const { memberId } = useParams();
  const { member: me } = useAuth();
  const [stats, setStats]       = useState(null);
  const [info, setInfo]         = useState(null);
  const [streaks, setStreaks]   = useState(null);
  const [heatmap, setHeatmap]   = useState(null);
  const [spider, setSpider]     = useState(null);
  const [pressure, setPressure] = useState(null);
  // Default to the current season for players still active. An ex-member has
  // no current-season figures, so 2026 would show them a page of zeros — they
  // land on All Time instead, which is the only view that has anything in it.
  const [year, setYear]         = useState('all');
  const [yearPinned, setYearPinned] = useState(false);
  const [tab, setTab]           = useState('batting');

  // Active players default to the current season once we know they're active.
  useEffect(() => {
    if (!yearPinned && info && info.is_active) setYear(new Date().getFullYear());
  }, [info, yearPinned]);
  const [loading, setLoading]   = useState(true);
  const [vizLoading, setVizLoading] = useState(false);
  // Drilldown state
  const [drill, setDrill] = useState(null); // { stat, filter }

  const openDrill = useCallback((stat, filter) => setDrill({ stat, filter: filter || null }), []);
  const closeDrill = useCallback(() => setDrill(null), []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`${PUB()}/stats-v2/player/${memberId}?year=${year}`),
      api.get(`${PUB()}/members/${memberId}`),
    ]).then(([s, m]) => { setStats(s.data); setInfo(m.data?.member); })
      .catch(() => {}).finally(() => setLoading(false));
    // Reset lazy data on year change
    setStreaks(null); setHeatmap(null); setSpider(null); setPressure(null);
  }, [memberId, year]);

  useEffect(() => {
    if (tab === 'vizz' && !heatmap) {
      setVizLoading(true);
      Promise.all([
        api.get(`${PUB()}/stats-v2/heatmap/${memberId}?year=${year}`),
        api.get(`${PUB()}/stats-v2/spider/${memberId}?year=${year}`),
        api.get(`${PUB()}/stats-v2/pressure/${memberId}?year=${year}`),
      ]).then(([hm, sp, pr]) => { setHeatmap(hm.data); setSpider(sp.data); setPressure(pr.data); })
        .catch(() => {}).finally(() => setVizLoading(false));
    }
    if (tab === 'form' && !streaks) {
      api.get(`${PUB()}/stats-v2/streaks/${memberId}?year=${year}`).then(r => setStreaks(r.data)).catch(() => {});
    }
  }, [tab, memberId, year]);

  const isMe = memberId === me?.id;
  const tabs = ['batting','bowling','fielding','form'];
  if (loading) return <><div style={{ background: NAVY, padding: 16, minHeight: 110 }} /><Loading /></>;
  if (!stats) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No data</div>;

  const b=stats.batting||{}, w=stats.bowling||{}, f=stats.fielding||{}, p=stats.points||{};
  const ph=b.phase||{}, bph=w.phase||{};
  const careerNote = year!=='all'&&b.career_runs_all_time>parseInt(b.runs||0)?`Career: ${b.career_runs_all_time}`:null;

  // ── Hero card derivations ──────────────────────────────────────
  const avgPos = parseFloat(b.avg_batting_position || b.avg_position || 0);
  const battingRole = avgPos > 0
    ? (avgPos <= 3 ? 'Top-order batter' : avgPos <= 6 ? 'Middle-order batter' : 'Lower-order batter')
    : (parseInt(b.innings||0) >= 5 ? 'Batter' : null);

  const bowlBalls = parseInt(w.balls_bowled || w.balls || 0);
  const matches   = parseInt(p.matches || 0);
  const ballsPerMatch = matches > 0 ? bowlBalls / matches : 0;
  const bowlingRole = bowlBalls === 0 ? null
    : ballsPerMatch >= 18 ? 'Main bowler'
    : ballsPerMatch >= 6  ? 'Part-time bowler'
    : 'Occasional bowler';

  const captainLed = parseInt(stats.captaincy?.matches_led || p.matches_captained || 0);
  const captainRole = captainLed > 0 ? `Captain · ${captainLed} led` : null;

  const isKeeper = parseInt(f.stumpings || 0) >= 3 || /†|keeper/i.test(info?.batting_hand || '');
  const keeperRole = isKeeper ? 'Wicket-keeper' : null;

  // Last-5 form dots from streaks data if available
  const last5 = Array.isArray(stats.recent_form) ? stats.recent_form.slice(-5)
              : Array.isArray(streaks?.last_five) ? streaks.last_five.slice(-5)
              : null;

  const battingHand = info?.batting_hand || null;

  return (
    <div style={S.page}>
      {/* ── Hero card ───────────────────────────────────────────── */}
      <div style={{ background: NAVY, color: 'white', padding: '14px 16px 12px' }}>
        <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
          <div style={{ width:52, height:52, borderRadius:'50%', border:`3px solid ${GOLD}`, background:'#2d4a8a', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.4rem', fontFamily:'Rajdhani,sans-serif', fontWeight:900, flexShrink:0 }}>
            {info?.display_name?.charAt(0)||'?'}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'baseline', gap:8, flexWrap:'wrap' }}>
              <span style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.2rem', color:GOLD, lineHeight:1.1 }}>
                {info?.display_name}
              </span>
              {battingHand && (
                <span style={{ fontSize:'0.7rem', color:'rgba(255,255,255,0.55)', fontWeight:500 }}>
                  {battingHand}
                </span>
              )}
              {isMe && <span style={{ fontSize:'0.7rem', color:'rgba(255,255,255,0.4)' }}>(You)</span>}
            </div>
            <div style={{ fontSize:'0.68rem', color:'rgba(255,255,255,0.5)', marginTop:1, letterSpacing:'0.04em' }}>
              {info?.ucc_id}{info?.jersey_no?` · #${info.jersey_no}`:''}{!info?.is_active?' · Inactive':''}
            </div>
            {/* Role badges */}
            <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginTop:8 }}>
              {battingRole && (
                <span style={{ padding:'3px 9px', borderRadius:11, fontSize:'0.65rem', fontWeight:500,
                  background:'rgba(78,160,255,0.18)', color:'#9CCAFF' }}>{battingRole}</span>
              )}
              {bowlingRole && (
                <span style={{ padding:'3px 9px', borderRadius:11, fontSize:'0.65rem', fontWeight:500,
                  background:'rgba(255,179,71,0.18)', color:'#FFCD86' }}>{bowlingRole}</span>
              )}
              {keeperRole && (
                <span style={{ padding:'3px 9px', borderRadius:11, fontSize:'0.65rem', fontWeight:500,
                  background:'rgba(150,255,170,0.18)', color:'#A6FBB9' }}>{keeperRole}</span>
              )}
              {captainRole && (
                <span onClick={() => openDrill('captain', null)}
                  style={{ padding:'3px 9px', borderRadius:11, fontSize:'0.65rem', fontWeight:500,
                  background:'rgba(255,94,154,0.18)', color:'#FFB8D1', cursor:'pointer' }}>{captainRole}</span>
              )}
            </div>
          </div>
          <div style={{ minWidth: 0, maxWidth: '52%' }}>
            {(() => {
              const years = (!year || year === 'all') ? { mode: 'all' }
                : String(year).includes(',') ? (() => {
                    const vals = String(year).split(',').map(s => parseInt(s,10)).filter(Boolean);
                    return vals.length === 1 ? { mode:'single', value: vals[0] } : { mode:'multi', values: vals };
                  })()
                : { mode: 'single', value: parseInt(year, 10) };
              const avail = stats.seasons || [2026,2025,2024,2023,2022,2021,2020,2019,2018,2017,2016,2015,2014,2013];
              const bridge = (next) => {
                setYearPinned(true);   // visitor chose — stop auto-switching
                if (!next || next.mode === 'all') setYear('all');
                else if (next.mode === 'single') setYear(String(next.value));
                else setYear((next.values || []).join(','));
              };
              return <YearChipsInline years={years} availableYears={avail} showAll onChange={bridge} />;
            })()}
          </div>
        </div>

        {/* Last 5 form dots */}
        {last5 && last5.length > 0 && (
          <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:12, paddingTop:10,
            borderTop:'0.5px solid rgba(255,255,255,0.08)' }}>
            <span style={{ fontSize:'0.62rem', color:'rgba(255,255,255,0.5)',
              textTransform:'uppercase', letterSpacing:'0.06em' }}>Last 5</span>
            <div style={{ display:'flex', gap:5 }}>
              {last5.map((r, i) => {
                const kind = (r?.result || r?.outcome || r || '').toString().toUpperCase();
                const isMOM = r?.motm || kind === 'MOM' || kind === '★';
                const isDNP = kind === 'DNP' || kind === '—' || kind === 'NULL' || !kind;
                const isW = kind === 'W' || kind === 'WIN';
                const isL = kind === 'L' || kind === 'LOSS';
                const cfg = isMOM ? { bg:GOLD, fg:NAVY, ch:'★' }
                  : isDNP ? { bg:'rgba(255,255,255,0.18)', fg:'rgba(255,255,255,0.6)', ch:'—' }
                  : isW ? { bg:'#16a34a', fg:'#fff', ch:'W' }
                  : isL ? { bg:'#dc2626', fg:'#fff', ch:'L' }
                  : { bg:'rgba(255,255,255,0.18)', fg:'#fff', ch:'?' };
                return (
                  <div key={i} title={r?.opponent || r?.date || ''}
                    style={{ width:22, height:22, borderRadius:'50%', background:cfg.bg, color:cfg.fg,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:'0.62rem', fontWeight:700 }}>
                    {cfg.ch}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 4 headline tiles */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:6, marginTop:12 }}>
          <StatTap v={p.total_points?Math.round(parseFloat(p.total_points)):0} l="Points" bg="rgba(255,255,255,0.1)" stat="total_points" onDrill={openDrill} />
          <StatTap v={p.matches||0} l="Matches" bg="rgba(255,255,255,0.1)" stat="innings" onDrill={openDrill} />
          <StatTap v={b.runs||0} l="Runs" bg="rgba(255,255,255,0.1)" stat="runs" onDrill={openDrill} />
          <StatTap v={w.wickets||0} l="Wickets" bg="rgba(255,255,255,0.1)" stat="wickets" onDrill={openDrill} />
        </div>
      </div>

      {/* 4 Tabs (collapsed from 6 — Phase + Vizz now fold into "More" inside each tab) */}
      <div style={{ display:'flex', background:'white', borderBottom:'1px solid #e5e7eb', position:'sticky', top:0, zIndex:10 }}>
        {tabs.map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={S.tab(tab===t)}>
            {{batting:'🏏',bowling:'🎳',fielding:'🧤',form:'📈'}[t]} {t}
          </button>
        ))}
      </div>

      <div style={{ padding:'12px 16px' }}>

        {tab==='batting'&&<>
          <div style={S.grid3}>
            <StatTap v={b.runs||0} l="Runs" sub={careerNote} stat="runs" onDrill={openDrill} />
            <StatTap v={b.highest_score||0} l="Highest" stat="highest_score" onDrill={openDrill} />
            <StatTap v={b.average||0} l="Average" stat="average" onDrill={openDrill} />
            <StatTap v={b.strike_rate||0} l="Strike Rate" stat="strike_rate" onDrill={openDrill} />
            <StatTap v={b.innings||0} l="Innings" stat="innings" onDrill={openDrill} />
            <StatTap v={b.not_outs||0} l="Not Outs" stat="not_outs" onDrill={openDrill} />
          </div>

          <div style={S.card}>
            <div style={S.hdr}>Boundaries & Milestones</div>
            <RowTap label="Fours" value={b.fours} emoji="4️⃣" stat="fours" onDrill={openDrill} />
            <RowTap label="Sixes" value={b.sixes} emoji="6️⃣" stat="sixes" onDrill={openDrill} />
            <RowTap label="Boundary %" value={b.boundary_pct?b.boundary_pct+'%':'—'} stat="fours" onDrill={openDrill} />
            <RowTap label="Half Centuries" value={b.fifties} emoji="🌟" stat="fifties" onDrill={openDrill} />
            <RowTap label="Centuries" value={b.centuries} emoji="💯" stat="centuries" onDrill={openDrill} />
            {b.fastest_fifty&&<RowTap label="Fastest 50" value={`${b.fastest_fifty.balls}b`} emoji="⚡" stat="fastest_fifty" onDrill={openDrill} />}
            {b.fastest_century&&<RowTap label="Fastest 100" value={`${b.fastest_century.balls}b`} emoji="🚀" stat="fastest_century" onDrill={openDrill} />}
          </div>

          <div style={S.card}>
            <div style={S.hdr}>Score Distribution</div>
            <RowTap label="Ducks" value={b.ducks} emoji="🦆" stat="ducks" onDrill={openDrill} />
            <RowTap label="Golden Ducks" value={b.golden_ducks} emoji="🥇" stat="golden_ducks" onDrill={openDrill} />
            <RowTap label="1-9" value={b.scores_1_9} stat="scores_1_9" onDrill={openDrill} />
            <RowTap label="10-19" value={b.scores_10_19} stat="scores_10_19" onDrill={openDrill} />
            <RowTap label="20-29" value={b.scores_20_29} stat="scores_20_29" onDrill={openDrill} />
            <RowTap label="30-49" value={b.scores_30_49} stat="scores_30_49" onDrill={openDrill} />
            <RowTap label="50-99" value={b.fifties} stat="fifties" onDrill={openDrill} />
            <RowTap label="100+" value={b.centuries} stat="centuries" onDrill={openDrill} />
            <RowTap label="Consistency (20+ %)" value={(b.consistency_pct||0)+'%'} stat="consistency_pct" onDrill={openDrill} />
          </div>

          {b.runs_trend?.length>1&&<div style={S.card}>
            <div style={S.hdr}><span>Run Trend</span><span style={{ fontSize:'0.65rem', color:'#94a3b8' }}>tap bar = innings detail</span></div>
            <BarChart data={b.runs_trend} xKey="match_date" yKey="runs" color={NAVY} />
          </div>}

          {b.dismissal_breakdown?.length>0&&<div style={S.card}>
            <div style={S.hdr}>How I Get Out</div>
            {b.dismissal_breakdown.map(d=>(
              <RowTap key={d.dismissal} label={d.dismissal.replace(/_/g,' ')} value={d.count} stat="dismissal_type" filter={d.dismissal} onDrill={openDrill} />
            ))}
          </div>}

          {b.by_position?.length>0&&<div style={S.card}>
            <div style={S.hdr}>By Batting Position</div>
            {b.by_position.map(pos=>(
              <div key={pos.pos} style={{ ...S.row, cursor:'pointer' }} onClick={()=>openDrill('by_position',String(pos.pos))}>
                <span style={S.label}>#{pos.pos}</span>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:'0.78rem', color:'#64748b' }}>{pos.innings} inn · avg {pos.avg_runs} · SR {pos.avg_sr}</span>
                  <span style={{ fontSize:'0.65rem', color:'#cbd5e1' }}>↗</span>
                </div>
              </div>
            ))}
          </div>}

          {b.wagon_zones?.length>0&&<div style={S.card}>
            <div style={S.hdr}><span>Wagon Wheel</span><span style={{ fontSize:'0.65rem', color:'#94a3b8' }}>tap zone for shots</span></div>
            <WagonWheel zones={b.wagon_zones} onDrill={openDrill} />
          </div>}

          <div style={S.card}>
            <div style={S.hdr}>Impact Stats</div>
            <RowTap label="Win % when scoring 30+"
              value={b.win_pct_with_30?.innings_30plus>0?`${Math.round(b.win_pct_with_30.wins/b.win_pct_with_30.innings_30plus*100)}%`:'—'}
              sub={b.win_pct_with_30?.innings_30plus>0?`${b.win_pct_with_30.wins}/${b.win_pct_with_30.innings_30plus} matches`:null}
              emoji="🏆" stat="win_pct_with_30" onDrill={openDrill} />
            <RowTap label="All-Round Matches (30+r & 2+w)" value={b.all_round_matches||0} emoji="⚡" stat="all_round_matches" onDrill={openDrill} />
          </div>
        </>}

        {tab==='bowling'&&<>
          <div style={S.grid3}>
            <StatTap v={w.wickets||0} l="Wickets" stat="wickets" onDrill={openDrill} />
            <StatTap v={w.economy||0} l="Economy" stat="economy" onDrill={openDrill} />
            <StatTap v={w.average||0} l="Average" stat="bowling_average" onDrill={openDrill} />
            <StatTap v={w.overs||0} l="Overs" stat="overs" onDrill={openDrill} />
            <StatTap v={w.maidens||0} l="Maidens" stat="maidens" onDrill={openDrill} />
            <StatTap v={w.maiden_pct?w.maiden_pct+'%':'—'} l="Maiden %" stat="maidens" onDrill={openDrill} />
          </div>

          <div style={S.card}>
            <div style={S.hdr}>Extras & Hauls</div>
            <RowTap label="Wides" value={w.wides} emoji="↔️" stat="wides" onDrill={openDrill} />
            <RowTap label="No Balls" value={w.noballs} emoji="⛔" stat="noballs" onDrill={openDrill} />
            <RowTap label="Runs Conceded" value={w.runs_conceded} stat="wickets" onDrill={openDrill} />
            <RowTap label="3-Wicket Hauls" value={w.three_wkt_hauls} emoji="🎯" stat="three_wkt_hauls" onDrill={openDrill} />
            <RowTap label="5-Wicket Hauls" value={w.five_wkt_hauls} emoji="🔥" stat="five_wkt_hauls" onDrill={openDrill} />
            <RowTap label="Dot Ball %" value={bph.dot_ball_pct?bph.dot_ball_pct+'%':'—'} stat="dot_ball_pct_bowl" onDrill={openDrill} />
            <RowTap label="Boundaries Conceded" value={bph.boundaries_conceded||0} stat="wickets" onDrill={openDrill} />
          </div>

          {w.best_figures&&<div style={S.card}>
            <div style={S.hdr}>Best Figures</div>
            <RowTap label="Best" value={`${w.best_figures.wickets}/${w.best_figures.runs} (${w.best_figures.overs}ov)`} emoji="⭐" stat="best_figures" onDrill={openDrill} />
            <RowTap label="All 3wkt+ hauls" value={`${w.three_wkt_hauls} hauls`} stat="three_wkt_hauls" onDrill={openDrill} />
          </div>}

          {w.wicket_types?.length>0&&<div style={S.card}>
            <div style={S.hdr}>How I Get Wickets</div>
            {w.wicket_types.map(d=>(
              <RowTap key={d.dismissal} label={d.dismissal.replace(/_/g,' ')} value={d.count} stat="wicket_type" filter={d.dismissal} onDrill={openDrill} />
            ))}
          </div>}

          {(w.worst_over||w.best_over)&&<div style={S.card}>
            <div style={S.hdr}>Extreme Overs</div>
            {w.worst_over&&<RowTap label="Most Expensive" value={`${w.worst_over.over_runs} runs (Ov ${w.worst_over.over_no ?? Number(w.worst_over.over_number) + 1}${w.worst_over.match_day ? ', ' + new Date(w.worst_over.match_day + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : ''})`} emoji="💥" stat="worst_over" onDrill={openDrill} />}
            {w.best_over&&<RowTap label="Most Economical" value={`${w.best_over.over_runs} runs (Ov ${w.best_over.over_no ?? Number(w.best_over.over_number) + 1}${w.best_over.match_day ? ', ' + new Date(w.best_over.match_day + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : ''})`} emoji="🧊" stat="best_over" onDrill={openDrill} />}
          </div>}
        </>}

        {tab==='fielding'&&<>
          <div style={S.grid3}>
            <StatTap v={f.catches||0} l="Catches" stat="catches" onDrill={openDrill} />
            <StatTap v={f.stumpings||0} l="Stumpings" stat="stumpings" onDrill={openDrill} />
            <StatTap v={f.run_outs||0} l="Run Outs" stat="run_outs" onDrill={openDrill} />
          </div>
          <div style={S.card}>
            <div style={S.hdr}>Summary</div>
            <RowTap label="Total Dismissals" value={parseInt(f.catches||0)+parseInt(f.stumpings||0)+parseInt(f.run_outs||0)} emoji="🤝" stat="total_dismissals" onDrill={openDrill} />
            <RowTap label="Best in a Match" value={f.most_catches_match||0} emoji="⭐" stat="catches" onDrill={openDrill} />
          </div>
        </>}

        {tab==='form'&&<>
          {b.recent_form?.length>0&&<div style={S.card}>
            <div style={S.hdr}>Recent Form (Last 5)</div>
            <div style={{ display:'flex', gap:8, padding:14, justifyContent:'center' }}>
              {b.recent_form.map((frm,i)=>(
                <div key={i} style={{ width:48, height:48, borderRadius:'50%', background:frm.runs>=30?GRN:frm.runs>=10?AMB:frm.runs===0?RED:'#94a3b8', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'0.95rem', color:'white', cursor:'pointer' }}
                  onClick={()=>openDrill('runs')}>
                  {frm.runs}{frm.dismissal==='not_out'?'*':''}
                </div>
              ))}
            </div>
          </div>}

          {b.runs_trend?.length>3&&<div style={S.card}>
            <div style={S.hdr}><span>Rolling Form (5-match avg)</span><span style={{ fontSize:'0.65rem', color:'#94a3b8', cursor:'pointer' }} onClick={()=>openDrill('runs')}>All innings ↗</span></div>
            <div style={{ padding:'8px 14px' }}><RollingFormChart data={b.runs_trend} /></div>
          </div>}

          {streaks?(<>
            <div style={{ ...S.secLbl }}>🔥 Streaks <span style={{ fontSize:'0.65rem', color:'#94a3b8', textTransform:'none', letterSpacing:0 }}>— tap to verify</span></div>
            <div style={S.grid2}>
              <StreakBadge label="Scoring 20+" current={streaks.streaks?.scoring_20_plus?.current||0} longest={streaks.streaks?.scoring_20_plus?.longest||0} emoji="📈" good stat="streak_scoring20" onDrill={openDrill} />
              <StreakBadge label="Ducks" current={streaks.streaks?.duck_streak?.current||0} longest={streaks.streaks?.duck_streak?.longest||0} emoji="🦆" good={false} stat="ducks" onDrill={openDrill} />
              <StreakBadge label="Any Runs" current={streaks.streaks?.any_runs?.current||0} longest={streaks.streaks?.any_runs?.longest||0} emoji="🏏" good stat="runs" onDrill={openDrill} />
              <StreakBadge label="50+ Scores" current={streaks.streaks?.fifty_streak?.current||0} longest={streaks.streaks?.fifty_streak?.longest||0} emoji="🌟" good stat="fifties" onDrill={openDrill} />
              <StreakBadge label="Wickets" current={streaks.streaks?.wicket_streak?.current||0} longest={streaks.streaks?.wicket_streak?.longest||0} emoji="🎳" good stat="streak_wickets" onDrill={openDrill} />
              <StreakBadge label="Wicket Drought" current={streaks.streaks?.wicket_drought?.current||0} longest={streaks.streaks?.wicket_drought?.longest||0} emoji="💤" good={false} stat="wickets" onDrill={openDrill} />
              <StreakBadge label="MOTM" current={streaks.streaks?.motm_streak?.current||0} longest={streaks.streaks?.motm_streak?.longest||0} emoji="🏅" good stat="streak_motm" onDrill={openDrill} />
              <StreakBadge label="No MOTM" current={streaks.streaks?.motm_drought?.current||0} longest={streaks.streaks?.motm_drought?.longest||0} emoji="😅" good={false} stat="motm_count" onDrill={openDrill} />
            </div>
            <div style={S.card}>
              <div style={S.hdr}>Rolling Averages</div>
              <RowTap label="Last 5 matches" value={streaks.rolling?.avg_last_5} stat="runs" onDrill={openDrill} />
              <RowTap label="Last 10 matches" value={streaks.rolling?.avg_last_10} stat="runs" onDrill={openDrill} />
            </div>
          </>):<Loading />}

          {stats.matchups?.nemesis?.length>0&&<div style={S.card}>
            <div style={S.hdr}>😈 Nemesis</div>
            {stats.matchups.nemesis.map(n=><RowTap key={n.id} label={n.display_name} value={`${n.times_out}×`} stat="dismissal_type" filter="all" onDrill={openDrill} />)}
          </div>}

          {/* ═══ Session 18 — Matchups & Directions ═══ */}
          {/* Hidden entirely if there's no ball-by-ball data for this player. */}
          {(stats.matchups?.direction_insight || stats.matchups?.bowler_insight || stats.matchups?.batter_insight) && (
            <>
              <div style={S.secLbl}>🎯 Matchups &amp; Directions <span style={{ fontSize:'0.65rem', color:'#94a3b8', textTransform:'none', letterSpacing:0 }}>— from ball-by-ball data</span></div>

              {/* ── Scoring directions ── */}
              {stats.matchups?.direction_insight && (
                <div style={S.card}>
                  <div style={S.hdr}>Scoring Directions</div>
                  <HiPair
                    leftLabel="Strongest" leftName={stats.matchups.direction_insight.strongest?.zone}
                    leftSub={`${stats.matchups.direction_insight.strongest?.runs||0} runs`}
                    rightLabel="Weakest" rightName={stats.matchups.direction_insight.weakest?.zone}
                    rightSub={`${stats.matchups.direction_insight.weakest?.runs||0} runs`}
                  />
                  <RowTap label="Most boundaries" value={stats.matchups.direction_insight.most_boundaries?.zone||'—'}
                    sub={`${stats.matchups.direction_insight.most_boundaries?.boundaries||0} fours+sixes`} />
                  <RowTap label="Most fours" value={stats.matchups.direction_insight.most_fours?.zone||'—'}
                    sub={`${stats.matchups.direction_insight.most_fours?.fours||0} fours`} />
                  <RowTap label="Most sixes" value={stats.matchups.direction_insight.most_sixes?.zone||'—'}
                    sub={`${stats.matchups.direction_insight.most_sixes?.sixes||0} sixes`} />
                </div>
              )}

              {/* ── Full direction breakdown table ── */}
              {stats.matchups?.direction_split?.length>0 && (
                <div style={S.card}>
                  <div style={S.hdr}><span>Runs by Direction</span><span style={{ fontSize:'0.65rem', color:'#94a3b8' }}>runs · 4s · 6s</span></div>
                  {stats.matchups.direction_split.map(z=>(
                    <div key={z.zone} style={S.row}>
                      <span style={S.label}>{z.zone}</span>
                      <span style={{ ...S.val, fontSize:'0.82rem' }}>
                        {z.runs} <span style={{ color:'#94a3b8', fontWeight:600 }}>· {z.fours}×4 · {z.sixes}×6</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Batting vs bowlers ── */}
              {stats.matchups?.bowler_insight && (
                <div style={S.card}>
                  <div style={S.hdr}>Batting — vs Bowlers</div>
                  <HiPair
                    leftLabel="Favourite" leftName={stats.matchups.bowler_insight.favourite?.display_name}
                    leftSub={`${stats.matchups.bowler_insight.favourite?.runs||0} runs off ${stats.matchups.bowler_insight.favourite?.balls||0}b`}
                    rightLabel="Keeps you quiet" rightName={stats.matchups.bowler_insight.kept_quiet_by?.display_name}
                    rightSub={`SR ${stats.matchups.bowler_insight.kept_quiet_by?.strike_rate||0}`}
                  />
                  {stats.matchups.bowler_insight.nemesis?.dismissals>0 && (
                    <RowTap label="Dismissed you most" value={stats.matchups.bowler_insight.nemesis?.display_name}
                      sub={`${stats.matchups.bowler_insight.nemesis?.dismissals}× out`} />
                  )}
                  <RowTap label="Most fours off" value={stats.matchups.bowler_insight.most_fours_off?.display_name||'—'}
                    sub={`${stats.matchups.bowler_insight.most_fours_off?.fours||0} fours`} />
                  <RowTap label="Most sixes off" value={stats.matchups.bowler_insight.most_sixes_off?.display_name||'—'}
                    sub={`${stats.matchups.bowler_insight.most_sixes_off?.sixes||0} sixes`} />
                </div>
              )}

              {/* ── Bowling vs batters (the mirror) ── */}
              {stats.matchups?.batter_insight && (
                <div style={S.card}>
                  <div style={S.hdr}>Bowling — vs Batters</div>
                  <HiPair
                    leftLabel="Your bunny" leftName={stats.matchups.batter_insight.bunny?.display_name}
                    leftSub={`${stats.matchups.batter_insight.bunny?.wickets||0} wkts`}
                    rightLabel="Punishes you" rightName={stats.matchups.batter_insight.punished_by?.display_name}
                    rightSub={`${stats.matchups.batter_insight.punished_by?.runs_conceded||0} runs`}
                  />
                  <RowTap label="Tightest against" value={stats.matchups.batter_insight.tightest_vs?.display_name||'—'}
                    sub={`econ ${stats.matchups.batter_insight.tightest_vs?.economy||0}`} />
                </div>
              )}

              {/* ── Bowling direction conceded ── */}
              {stats.matchups?.conceded_direction?.length>0 && (
                <div style={S.card}>
                  <div style={S.hdr}><span>Conceded by Direction</span><span style={{ fontSize:'0.65rem', color:'#94a3b8' }}>where batters score off you</span></div>
                  {stats.matchups.conceded_direction.map(z=>(
                    <div key={z.zone} style={S.row}>
                      <span style={S.label}>{z.zone}</span>
                      <span style={{ ...S.val, fontSize:'0.82rem' }}>
                        {z.runs_conceded} <span style={{ color:'#94a3b8', fontWeight:600 }}>· {z.boundaries_conceded} bnd</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          <div style={S.card}>
            <div style={S.hdr}>Points Summary</div>
            <RowTap label="Total Points" value={p.total_points?Math.round(parseFloat(p.total_points)):0} stat="total_points" onDrill={openDrill} />
            <RowTap label="Avg per Match" value={p.avg_points||'—'} stat="avg_points" onDrill={openDrill} />
            <RowTap label="MOTM Awards" value={p.motm_count} emoji="🏅" stat="motm_count" onDrill={openDrill} />
          </div>

          {/* Session 17 — Season Penalties section, only shown if penalties exist */}
          {(p.penalty_pts > 0) && (
            <div style={S.card}>
              <div style={S.hdr}>
                <span>Season Penalties</span>
                <span style={{ fontSize:'0.7rem', color:'#dc2626', fontWeight:700 }}>−{p.penalty_pts}</span>
              </div>
              <div style={{ padding:'8px 14px 4px', fontSize:'0.7rem', color:'#6b7280' }}>
                Gross points: {Math.round(parseFloat(p.gross_points || 0))} · After penalty: {Math.round(parseFloat(p.total_points || 0))}
              </div>
              <div style={{ padding:'4px 14px 12px' }}>
                {(p.penalty_breakdown || []).map(item => (
                  <div key={item.id} style={{
                    display:'flex', justifyContent:'space-between', alignItems:'flex-start',
                    padding:'8px 0', borderBottom:'1px solid #f1f5f9', gap:10,
                  }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'0.78rem', color:'#374151' }}>{item.reason}</div>
                      <div style={{ fontSize:'0.62rem', color:'#9ca3af', marginTop:2 }}>
                        {item.year} · {item.source === 'fine' ? 'Fine' : 'Adjustment'}
                      </div>
                    </div>
                    <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.95rem', color:'#dc2626', flexShrink:0 }}>
                      −{item.penalty_pts}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>}

        {tab==='phase'&&<>
          <div style={S.card}>
            <div style={S.hdr}><span>Batting by Phase</span><span style={{ fontSize:'0.65rem', color:'#94a3b8' }}>tap phase to verify</span></div>
            <div style={{ paddingTop:12 }}>
              <PhaseBar label="Powerplay" runs={ph.powerplay?.runs||0} balls={ph.powerplay?.balls||0} color="#2563eb" statKey="phase_powerplay" onDrill={openDrill} />
              <PhaseBar label="Middle Overs" runs={ph.middle?.runs||0} balls={ph.middle?.balls||0} color={GRN} statKey="phase_middle" onDrill={openDrill} />
              <PhaseBar label="Death Overs" runs={ph.death?.runs||0} balls={ph.death?.balls||0} color={RED} statKey="phase_death" onDrill={openDrill} />
            </div>
            <div style={{ padding:'4px 14px 14px', display:'flex', gap:20, justifyContent:'center' }}>
              <DotRing pct={ph.dot_ball_pct||0} label="Dot Ball %" color={RED} stat="dot_ball_pct_bat" onDrill={openDrill} />
              <DotRing pct={b.boundary_pct||0} label="Boundary %" color={GRN} stat="fours" onDrill={openDrill} />
            </div>
          </div>

          <div style={S.card}>
            <div style={S.hdr}><span>Bowling by Phase</span><span style={{ fontSize:'0.65rem', color:'#94a3b8' }}>tap to verify</span></div>
            <RowTap label="Powerplay" value={bph.powerplay_economy??'—'} emoji="🔵" stat="phase_bowl_pp" onDrill={openDrill} />
            <RowTap label="Middle Overs" value={bph.middle_economy??'—'} emoji="🟢" stat="phase_bowl_mid" onDrill={openDrill} />
            <RowTap label="Death Overs" value={bph.death_economy??'—'} emoji="🔴" stat="phase_bowl_death" onDrill={openDrill} />
            <div style={{ padding:'4px 14px 14px', display:'flex', gap:20, justifyContent:'center' }}>
              <DotRing pct={bph.dot_ball_pct||0} label="Dot Ball %" color={GRN} stat="dot_ball_pct_bowl" onDrill={openDrill} />
            </div>
          </div>
        </>}

        {tab==='vizz'&&<>
          {vizLoading?<Loading />:<>
            {spider?.players?.[0]&&<div style={S.card}>
              <div style={S.hdr}>Performance Profile (Radar)</div>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'12px 0 6px' }}>
                <RadarChart players={spider.players} size={220} />
              </div>
              <div style={{ padding:'0 14px 12px', display:'flex', flexWrap:'wrap', gap:6, justifyContent:'center' }}>
                {Object.values(spider.players[0]?.dimensions||{}).map(d=>(
                  <div key={d.label} style={{ background:'#f0f9ff', borderRadius:8, padding:'4px 10px', fontSize:'0.68rem', fontWeight:700, color:NAVY }}>
                    {d.label}: <span style={{ color:d.score>=70?GRN:d.score>=40?AMB:RED }}>{d.score}</span>
                  </div>
                ))}
              </div>
            </div>}

            {heatmap?.batting?.cells?.length>0&&<div style={S.card}>
              <div style={S.hdr}><span>Batting Heat Map</span><span style={{ fontSize:'0.65rem', color:'#94a3b8' }}>avg runs per ball position</span></div>
              <OverHeatMap data={heatmap.batting.cells} label="Batting" />
            </div>}

            {heatmap?.bowling?.cells?.length>0&&<div style={S.card}>
              <div style={S.hdr}><span>Bowling Heat Map</span><span style={{ fontSize:'0.65rem', color:'#94a3b8' }}>avg runs conceded per ball</span></div>
              <OverHeatMap data={heatmap.bowling.cells} label="Bowling" />
            </div>}

            {heatmap?.batting?.over_strip?.length>0&&<div style={S.card}>
              <div style={S.hdr}><span>Batting by Over</span><span style={{ fontSize:'0.65rem', color:'#94a3b8', cursor:'pointer' }} onClick={()=>openDrill('phase_powerplay')}>PP ↗</span></div>
              <BarChart data={heatmap.batting.over_strip} xKey="over_number" yKey="runs" color="#2563eb" />
            </div>}

            {heatmap?.bowling?.over_strip?.length>0&&<div style={S.card}>
              <div style={S.hdr}><span>Bowling by Over</span><span style={{ fontSize:'0.65rem', color:'#94a3b8', cursor:'pointer' }} onClick={()=>openDrill('worst_over')}>Expensive ↗</span></div>
              <BarChart data={heatmap.bowling.over_strip} xKey="over_number" yKey="runs" color={RED} />
            </div>}

            {pressure&&<div style={S.card}>
              <div style={S.hdr}>Pressure Situations</div>
              <RowTap label="SR in Powerplay" value={pressure.powerplay_batting?.sr||0} sub={`${pressure.powerplay_batting?.balls||0} balls`} stat="pressure_pp_bat" onDrill={openDrill} />
              <RowTap label="SR in Death Overs" value={pressure.death_batting?.sr||0} sub={`${pressure.death_batting?.balls||0} balls`} stat="phase_death" onDrill={openDrill} />
              <RowTap label="First Ball Dismissals" value={pressure.first_ball?.dismissed_first_ball||0} sub={`of ${pressure.first_ball?.total||0} innings`} stat="pressure_first_ball" onDrill={openDrill} />
              <RowTap label="Top-Order Avg (1-4)" value={pressure.positional?.top_order_avg||'—'} stat="by_position" filter="3" onDrill={openDrill} />
              <RowTap label="Lower-Order Avg (5+)" value={pressure.positional?.lower_order_avg||'—'} stat="by_position" filter="6" onDrill={openDrill} />
              <RowTap label="Boundary % after a Dot" value={pressure.after_dot_ball?.boundary_pct?pressure.after_dot_ball.boundary_pct+'%':'—'} stat="dot_ball_pct_bat" onDrill={openDrill} />
            </div>}
          </>}
        </>}
      </div>

      {/* Drilldown Sheet */}
      {drill && (
        <DrilldownSheet memberId={memberId} year={year} stat={drill.stat} filter={drill.filter} onClose={closeDrill} />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// RECORDS PAGE
// ═══════════════════════════════════════════════════════════════
export function RecordsPage({ publicMode = false }) {
  const [data, setData]   = useState(null);
  const [year, setYear]   = useState(2026);
  const [tab, setTab]     = useState('batting');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [drilldown, setDrilldown] = useState(null);
  const [batSubTab, setBatSubTab] = useState('leaderboard'); // { stat, id, id2, name, name2 }
  const [ddData, setDdData] = useState(null);
  const [ddLoading, setDdLoading] = useState(false);
  // Auto-reset to leaderboard if user switches to a tab that doesn't have current subtab
  useEffect(() => {
    if ((tab === 'keeper' || tab === 'fielding') && batSubTab === 'phase') {
      setBatSubTab('leaderboard');
    }
    if ((tab === 'bowling' || tab === 'keeper' || tab === 'fielding') && batSubTab === 'partnerships') {
      setBatSubTab('leaderboard');
    }
  }, [tab, batSubTab]);
  // Session 16 Pass 2 — scroll-collapse + filter modal
  const collapsed = useCollapseOnScroll(120);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`${PUB()}/stats-v2/records?year=${year}&includeInactive=${includeInactive}`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [year, includeInactive]);

  function openDrilldown(stat, id, name, id2, name2) {
    setDrilldown({ stat, id, id2, name, name2 });
    setDdData(null);
    setDdLoading(true);
    const params = new URLSearchParams({ stat, id, year });
    if (id2) params.set('id2', id2);
    api.get(`${PUB()}/stats-v2/records-drilldown?${params}`)
      .then(r => setDdData(r.data))
      .catch(() => setDdData({ title: 'Error', columns: [], rows: [] }))
      .finally(() => setDdLoading(false));
  }

  const tabs = ['batting','bowling','keeper','fielding','overall'];

  // ── Leaderboard Row ─────────────────────────────────────────
  const LBRow = ({ r, i }) => (
    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 14px',
      borderBottom:'1px solid #f1f5f9', background: i===0?'#fffbeb':i<3?'#f8fafc':'white' }}>
      <div style={{ width:24, height:24, borderRadius:'50%', background:i===0?'#f59e0b':i===1?'#9ca3af':i===2?'#b45309':'#e5e7eb',
        display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.7rem', fontWeight:700, color:'white', flexShrink:0 }}>
        {i+1}
      </div>
      <Link to={`${publicMode ? "/club/stats" : "/stats-v2"}/${r.id}`} style={{ flex:1, fontWeight:700, fontSize:'0.85rem', color:NAVY, textDecoration:'none' }}>
        {r.display_name}
      </Link>
      <div style={{ textAlign:'right' }}>
        <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.05rem', color:NAVY }}>{r.runs}</div>
        <div style={{ fontSize:'0.6rem', color:'#9ca3af' }}>runs</div>
      </div>
      <div style={{ textAlign:'right', minWidth:38 }}>
        <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#475569' }}>{r.average||'—'}</div>
        <div style={{ fontSize:'0.6rem', color:'#9ca3af' }}>avg</div>
      </div>
      <div style={{ textAlign:'right', minWidth:38 }}>
        <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#475569' }}>{r.sr||'—'}</div>
        <div style={{ fontSize:'0.6rem', color:'#9ca3af' }}>SR</div>
      </div>
      <div style={{ textAlign:'right', minWidth:28 }}>
        <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#475569' }}>{r.matches}</div>
        <div style={{ fontSize:'0.6rem', color:'#9ca3af' }}>m</div>
      </div>
    </div>
  );

  // ── Record Table ─────────────────────────────────────────────
  const RecordTable = ({ rows, emoji, title, valueFn, subFn, stat, pairMode, nameOverride, note }) => {
    if (!rows || rows.length === 0) return null;
    return (
      <div style={{ background:'white', borderRadius:12, overflow:'hidden',
        boxShadow:'0 1px 4px rgba(0,0,0,0.06)', marginBottom:12 }}>
        <div style={{ background:'linear-gradient(135deg,#1E3A6E,#2d5a9e)', padding:'10px 14px',
          fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.95rem', color:'white' }}>
          {emoji} {title}
        </div>
        {note && (
          <div style={{ padding:'7px 14px', fontSize:'0.72rem', color:'#64748b',
            background:'#f8fafc', borderBottom:'1px solid #f1f5f9', lineHeight:1.35 }}>
            {note}
          </div>
        )}
        {rows.map((r, i) => {
          const name = nameOverride ? nameOverride(r) : pairMode ? `${r.p1_name || '?'} & ${r.p2_name || '?'}` : r.display_name;
          const val = String(valueFn(r) ?? '—');
          const sub = subFn ? subFn(r) : null;
          
          // Show rank number only for first occurrence of each score
          const showRankNumber = i === 0 || String(valueFn(rows[i-1]) ?? '—') !== val;
          const currentRank = r.rank || (i + 1);
          
          return (
            <div key={i} onClick={() => pairMode
              ? openDrilldown(stat, r.p1_id, r.p1_name, r.p2_id, r.p2_name)
              : nameOverride && r.p1_id
              ? openDrilldown(stat, r.p1_id, r.p1_name, r.partnership_id, r.p2_name)
              : openDrilldown(stat, r.id, r.display_name)}
              style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 14px',
                borderBottom: i < rows.length-1 ? '1px solid #f1f5f9' : 'none',
                background: i===0 ? '#f8fafc' : 'white', cursor:'pointer' }}>
              <div style={{ width:22, textAlign:'center', fontFamily:'Rajdhani,sans-serif',
                fontWeight:800, fontSize:'0.78rem', color: i===0?GOLD:'#9ca3af', flexShrink:0 }}>
                {showRankNumber ? currentRank : ''}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:700, fontSize:'0.85rem', color:NAVY,
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{name}</div>
                {sub && <div style={{ fontSize:'0.68rem', color:'#9ca3af', marginTop:1 }}>{sub}</div>}
              </div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr auto', 
                alignItems: 'center', 
                justifyContent: 'end',
                minWidth: '70px',
                gap: '1px',
                flexShrink: 0 
              }}>
                <div style={{ 
                  fontFamily: 'Courier New, monospace', 
                  fontWeight: 900, 
                  fontSize: '1.05rem',
                  color: i===0 ? GOLD : '#334155',
                  textAlign: 'right',
                  justifySelf: 'end'
                }}>
                  {val.includes('*') ? val.slice(0, -1) : val}
                </div>
                <div style={{ 
                  fontFamily: 'Courier New, monospace', 
                  fontWeight: 900, 
                  fontSize: '1.05rem',
                  color: i===0 ? GOLD : '#334155',
                  width: '8px',
                  textAlign: 'left'
                }}>
                  {val.includes('*') ? '*' : ''}
                </div>
              </div>
              <div style={{ color:'#9ca3af', fontSize:'0.75rem', flexShrink:0 }}>›</div>
            </div>
          );
        })}
      </div>
    );
  };

  const SectionHeader = ({ title }) => (
    <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'0.8rem',
      color:'#64748b', textTransform:'uppercase', letterSpacing:'0.08em',
      padding:'16px 0 6px' }}>{title}</div>
  );

  const d = data?.[tab] || {};
  const tabLabelMap = { batting: '🏏 Batting', bowling: '🎳 Bowling', keeper: '🥅 Keeper', fielding: '🧤 Fielding', overall: '🏆 Overall' };
  const subTabMap = {
    leaderboard: '🏅 Leaderboard', innings: '🏏 Innings', career: '📊 Career', power: '💥 Power',
    partnerships: '🤝 Pairs', consistency: '📈 Consistency', situations: '🎯 Situations', phase: '⚡ Phase',
  };
  const yearBadge = (!year || year === 'all') ? 'All Time'
    : String(year).includes(',') ? (String(year).split(',').length + ' Seasons')
    : String(year);

  return (
    <div style={S.page}>
      {/* Navy hero — scrolls away naturally */}
      <div style={{ background:NAVY, color:'white', padding:16 }}>
        <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.3rem', marginBottom:12 }}>
          🏆 UCC Records
        </div>
        <Controls year={year} setYear={setYear} includeInactive={includeInactive}
          setIncludeInactive={setIncludeInactive} showAll />
      </div>

      {/* Sticky tabs + subtab pills — CSS only, no scroll listener */}
      <div style={{ position:'sticky', top:0, zIndex:50, background:'white',
        boxShadow:'0 2px 8px rgba(0,0,0,0.08)' }}>
        {/* Tabs */}
        <div style={{ display:'flex', background:'white', borderBottom:'1px solid #e5e7eb',
          overflowX:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
          {['batting','bowling','keeper','fielding','overall'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{ ...S.tab(tab===t), flexShrink:0, whiteSpace:'nowrap' }}>
              {{'batting':'🏏','bowling':'🎳','keeper':'🥅','fielding':'🧤','overall':'🏆'}[t]} {t === 'keeper' ? 'WK' : t}
            </button>
          ))}
        </div>

          {/* Sub-tab pills — render for all tabs */}
          <div style={{ background:'white', borderBottom:'1px solid #f1f5f9',
            padding:'8px 16px', display:'flex', gap:6, overflowX:'auto',
            scrollbarWidth:'none', msOverflowStyle:'none' }}>
              {[
                { key:'leaderboard', label:'🏅 Leaderboard' },
                { key:'innings',     label:'🏏 Innings' },
                { key:'career',      label:'📊 Career' },
                { key:'power',       label:'💥 Power' },
                { key:'partnerships',label:'🤝 Pairs' },
                { key:'consistency', label:'📈 Consistency' },
                { key:'situations',  label:'🎯 Situations' },
                { key:'phase',       label:'⚡ Phase' },
              ].filter(s => {
                if ((tab === 'keeper' || tab === 'fielding') && s.key === 'phase') return false;
                if ((tab === 'bowling' || tab === 'keeper' || tab === 'fielding') && s.key === 'partnerships') return false;
                return true;
              }).map(s => (
                <button key={s.key} onClick={() => setBatSubTab(s.key)} style={{
                  flexShrink:0, padding:'6px 14px', borderRadius:20,
                  border: batSubTab===s.key ? 'none' : '1px solid #e5e7eb',
                  background: batSubTab===s.key ? NAVY : 'white',
                  color: batSubTab===s.key ? 'white' : '#475569',
                  fontWeight: batSubTab===s.key ? 700 : 500,
                  fontSize:'0.78rem', cursor:'pointer',
                }}>
                  {s.label}
                </button>
              ))}
            </div>
      </div>{/* end sticky tabs */}

      {/* Filter modal */}
      <FilterSheet open={filterOpen} onClose={() => setFilterOpen(false)} title="Filters">
        <Controls year={year} setYear={setYear} includeInactive={includeInactive}
          setIncludeInactive={setIncludeInactive} showAll />
        <div style={{ height: 14 }} />
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>CATEGORY</div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom: 10 }}>
          {['batting','bowling','keeper','fielding','overall'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding:'6px 12px', borderRadius:18, border:'none',
              background: tab===t ? '#1E3A6E' : '#f1f5f9',
              color: tab===t ? 'white' : '#6b7280',
              fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.76rem',
              cursor:'pointer', textTransform:'capitalize',
            }}>
              {{'batting':'🏏','bowling':'🎳','keeper':'🥅','fielding':'🧤','overall':'🏆'}[t]} {t === 'keeper' ? 'WK' : t}
            </button>
          ))}
        </div>
        {tab === 'batting' && (
          <>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>SUBCATEGORY</div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {Object.entries(subTabMap).map(([key, label]) => (
                <button key={key} onClick={() => setBatSubTab(key)} style={{
                  padding:'6px 12px', borderRadius:18, border:'none',
                  background: batSubTab===key ? '#1E3A6E' : '#f1f5f9',
                  color: batSubTab===key ? 'white' : '#6b7280',
                  fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.76rem', cursor:'pointer',
                }}>{label}</button>
              ))}
            </div>
          </>
        )}
      </FilterSheet>

      {loading ? <Loading /> : (
        <div style={{ padding:'0 16px 80px' }}>
          {tab === 'batting' && <>

            {batSubTab === 'leaderboard' && <>
            {/* Full Leaderboard */}
            <div style={{ background:'white', borderRadius:12, overflow:'hidden',
              boxShadow:'0 1px 4px rgba(0,0,0,0.06)', marginBottom:12 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 14px',
                background:'#f8fafc', borderBottom:'1px solid #e5e7eb' }}>
                <div style={{ width:24 }}></div>
                <div style={{ flex:1, fontSize:'0.65rem', color:'#9ca3af', fontWeight:600 }}>PLAYER</div>
                <div style={{ width:36, textAlign:'right', fontSize:'0.65rem', color:'#9ca3af', fontWeight:600 }}>RUNS</div>
                <div style={{ width:38, textAlign:'right', fontSize:'0.65rem', color:'#9ca3af', fontWeight:600 }}>AVG</div>
                <div style={{ width:38, textAlign:'right', fontSize:'0.65rem', color:'#9ca3af', fontWeight:600 }}>SR</div>
                <div style={{ width:28, textAlign:'right', fontSize:'0.65rem', color:'#9ca3af', fontWeight:600 }}>M</div>
              </div>
              {(data?.leaderboard || []).map((r, i) => <LBRow key={r.id} r={r} i={i} />)}
            </div>

            {/* Individual Innings */}
            </>
          }
          {batSubTab === 'innings' && <>
            <SectionHeader title="Individual Innings Records" />
            <RecordTable emoji="🏏" title="Highest Individual Score" rows={d.highest_score} stat="highest_score"
              valueFn={r => `${r.runs}${r.dismissal==='not_out'?'*':''}`} subFn={r => `${r.balls}b · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="💯" title="Fastest Century" rows={d.fastest_century} stat="fastest_century"
              valueFn={r => `${r.balls}b`} subFn={r => `${r.runs}r · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="🌟" title="Fastest Fifty" rows={d.fastest_fifty} stat="fastest_fifty"
              valueFn={r => `${r.balls}b`} subFn={r => `${r.runs}r · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="👆" title="Highest Score by Opener (pos 1-2)" rows={d.highest_by_opener} stat="highest_by_opener"
              valueFn={r => `${r.runs}${r.dismissal==='not_out'?'*':''}`} subFn={r => `pos ${r.batting_position} · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="🎯" title="Highest Score at 3-5" rows={d.highest_mid_order} stat="highest_mid_order"
              valueFn={r => `${r.runs}${r.dismissal==='not_out'?'*':''}`} subFn={r => `pos ${r.batting_position} · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="🔻" title="Highest Score by Lower Order (pos 6+)" rows={d.highest_lower_order} stat="highest_lower_order"
              valueFn={r => `${r.runs}${r.dismissal==='not_out'?'*':''}`} subFn={r => `pos ${r.batting_position} · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="💪" title="Highest Score in a Losing Cause" rows={d.highest_in_loss} stat="highest_in_loss"
              valueFn={r => `${r.runs}${r.dismissal==='not_out'?'*':''}`} subFn={r => `${r.balls}b · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="🧱" title="Highest Score Without a Boundary" rows={d.highest_no_boundary} stat="highest_no_boundary"
              valueFn={r => `${r.runs}${r.dismissal==='not_out'?'*':''}`} subFn={r => `${r.balls}b · ${fmtDate(r.match_date)}`} />

            {/* Career Records */}
            </>
          }
          {batSubTab === 'career' && <>
            <SectionHeader title="Career Batting Records" />
            <RecordTable emoji="📐" title="Best Batting Average (min 5 dismissals)" rows={d.best_average} stat="best_average"
              valueFn={r => `avg ${r.average}`} subFn={r => `${r.runs}r · ${r.innings} inn`} />
            <RecordTable emoji="⚡" title="Best Strike Rate (min 50 balls)" rows={d.best_sr} stat="best_sr"
              valueFn={r => `SR ${r.sr}`} subFn={r => `${r.runs}r · ${r.balls}b`} />
            <RecordTable emoji="🌟" title="Most Fifties" rows={d.most_fifties} stat="most_fifties"
              valueFn={r => `${r.fifties}×`} />
            <RecordTable emoji="💯" title="Most Centuries" rows={d.most_centuries} stat="most_centuries"
              valueFn={r => `${r.centuries}×`} />
            <RecordTable emoji="4️⃣" title="Most Fours" rows={d.most_fours} stat="most_fours"
              valueFn={r => r.fours} subFn={r => `${r.runs} total runs`} />
            <RecordTable emoji="6️⃣" title="Most Sixes" rows={d.most_sixes} stat="most_sixes"
              valueFn={r => r.sixes} subFn={r => `${r.runs} total runs`} />
            <RecordTable emoji="🏃" title="Most Balls Faced" rows={d.most_balls_faced} stat="most_balls_faced"
              valueFn={r => `${r.balls}b`} subFn={r => `${r.runs}r · ${r.innings} inn`} />
            <RecordTable emoji="🛡️" title="Most Not Outs" rows={d.most_not_outs} stat="most_not_outs"
              valueFn={r => `${r.not_outs}×`} subFn={r => `${r.runs} runs`} />
            <RecordTable emoji="💥" title="Most Runs from Boundaries" rows={d.most_boundary_runs} stat="most_boundary_runs"
              valueFn={r => `${r.boundary_runs}r`} subFn={r => `${r.fours}×4 + ${r.sixes}×6`} />

            {/* Power Hitting */}
            </>
          }
          {batSubTab === 'power' && <>
            <SectionHeader title="Power Hitting" />
            <RecordTable emoji="🎆" title="Most Boundaries in Single Innings" rows={d.most_boundaries_innings} stat="most_boundaries_innings"
              valueFn={r => `${r.boundaries} bdry`} subFn={r => `${r.fours}×4 ${r.sixes}×6 · ${r.runs}r · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="6️⃣" title="Most Sixes in Single Innings" rows={d.most_sixes_innings} stat="most_sixes_innings"
              valueFn={r => `${r.sixes}×6`} subFn={r => `${r.runs}r · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="📊" title="Highest Boundary % (min 50 balls)" rows={d.highest_boundary_pct} stat="highest_boundary_pct"
              valueFn={r => `${r.boundary_pct}%`} subFn={r => `${r.runs}r · ${r.balls}b`} />

            {/* Partnerships */}
            </>
          }
          {batSubTab === 'partnerships' && <>
            <SectionHeader title="Partnerships" />
            {/* Highest by wicket */}
            <RecordTable emoji="🤝" title="Highest Partnership (Any Wicket)" rows={d.highest_partnership} stat="highest_partnership"
              valueFn={r => `${r.runs}r`} subFn={r => `wkt ${r.wicket} · ${fmtDate(r.match_date)}`}
              nameOverride={r => `${r.p1_name || ""} & ${r.p2_name || ""}`} pairMode={false} />
            <RecordTable emoji="🏁" title="Highest Opening (1st Wicket) Partnership" rows={d.highest_opening_partner} stat="highest_opening_partner"
              valueFn={r => `${r.runs}r`} subFn={r => fmtDate(r.match_date)}
              nameOverride={r => `${r.p1_name || ""} & ${r.p2_name || ""}`} pairMode={false} />
            <RecordTable emoji="2️⃣" title="Highest 2nd Wicket Partnership" rows={d.highest_wkt_2} stat="highest_wkt_2"
              valueFn={r => `${r.runs}r`} subFn={r => fmtDate(r.match_date)}
              nameOverride={r => `${r.p1_name || ""} & ${r.p2_name || ""}`} pairMode={false} />
            <RecordTable emoji="3️⃣" title="Highest 3rd Wicket Partnership" rows={d.highest_wkt_3} stat="highest_wkt_3"
              valueFn={r => `${r.runs}r`} subFn={r => fmtDate(r.match_date)}
              nameOverride={r => `${r.p1_name || ""} & ${r.p2_name || ""}`} pairMode={false} />
            <RecordTable emoji="4️⃣" title="Highest 4th Wicket Partnership" rows={d.highest_wkt_4} stat="highest_wkt_4"
              valueFn={r => `${r.runs}r`} subFn={r => fmtDate(r.match_date)}
              nameOverride={r => `${r.p1_name || ""} & ${r.p2_name || ""}`} pairMode={false} />
            <RecordTable emoji="5️⃣" title="Highest 5th Wicket Partnership" rows={d.highest_wkt_5} stat="highest_wkt_5"
              valueFn={r => `${r.runs}r`} subFn={r => fmtDate(r.match_date)}
              nameOverride={r => `${r.p1_name || ""} & ${r.p2_name || ""}`} pairMode={false} />
            <RecordTable emoji="🔻" title="Highest Lower Order Partnership (6th Wkt+)" rows={d.highest_wkt_6plus} stat="highest_wkt_6plus"
              valueFn={r => `${r.runs}r`} subFn={r => `wkt ${r.wicket} · ${fmtDate(r.match_date)}`}
              nameOverride={r => `${r.p1_name || ""} & ${r.p2_name || ""}`} pairMode={false} />

            {/* Speed records */}
            <RecordTable emoji="⚡" title="Fastest 50 Partnership" rows={d.fastest_50_partnership} stat="fastest_50_partnership"
              valueFn={r => `${r.balls}b`} subFn={r => `${r.runs}r · wkt ${r.wicket} · ${fmtDate(r.match_date)}`}
              nameOverride={r => `${r.p1_name || ''} & ${r.p2_name || ''}`} pairMode={false} />
            <RecordTable emoji="🚀" title="Fastest 100 Partnership" rows={d.fastest_100_partnership} stat="fastest_100_partnership"
              valueFn={r => `${r.balls}b`} subFn={r => `${r.runs}r · wkt ${r.wicket} · ${fmtDate(r.match_date)}`}
              nameOverride={r => `${r.p1_name || ''} & ${r.p2_name || ''}`} pairMode={false} />

            {/* Situation records */}
            <RecordTable emoji="🏃" title="Highest Partnership in Chase (2nd Innings)" rows={d.highest_chase_partner} stat="highest_chase_partner"
              valueFn={r => `${r.runs}r`} subFn={r => `wkt ${r.wicket} · ${fmtDate(r.match_date)}`}
              nameOverride={r => `${r.p1_name || ''} & ${r.p2_name || ''}`} pairMode={false} />
            <RecordTable emoji="🏏" title="Highest Partnership in 1st Innings" rows={d.highest_first_inn_partner} stat="highest_first_inn_partner"
              valueFn={r => `${r.runs}r`} subFn={r => `wkt ${r.wicket} · ${fmtDate(r.match_date)}`}
              nameOverride={r => `${r.p1_name || ''} & ${r.p2_name || ''}`} pairMode={false} />
            <RecordTable emoji="🏆" title="Highest Match-Winning Partnership" rows={d.highest_winning_partner} stat="highest_winning_partner"
              valueFn={r => `${r.runs}r`} subFn={r => `wkt ${r.wicket} · Inn ${r.innings} · ${fmtDate(r.match_date)}`}
              nameOverride={r => `${r.p1_name || ''} & ${r.p2_name || ''}`} pairMode={false} />

            {/* Career aggregates */}
            <RecordTable emoji="💑" title="Most Runs Together" rows={d.best_pairs} stat="best_pairs"
              valueFn={r => `${r.total_runs}r`} subFn={r => `${r.partnerships} innings · best ${r.best}`}
              pairMode={true} />
            <RecordTable emoji="📈" title="Best Partnership Average (min 10 innings)" rows={d.best_partnership_avg} stat="best_partnership_avg"
              valueFn={r => `avg ${r.average}`} subFn={r => `${r.partnerships} innings · ${r.total_runs}r`}
              pairMode={true} />
            <RecordTable emoji="📅" title="Most Innings Batting Together" rows={d.most_innings_together} stat="most_innings_together"
              valueFn={r => `${r.count}×`} subFn={r => `${r.total_runs} total runs`}
              pairMode={true} />
            <RecordTable emoji="💯" title="Most Century Partnerships (100+)" rows={d.most_century_partnerships} stat="most_century_partnerships"
              valueFn={r => `${r.count}×`} subFn={r => null}
              pairMode={true} />
            <RecordTable emoji="⭐" title="Most Half-Century Partnerships (50+)" rows={d.most_50_partnerships} stat="most_50_partnerships"
              valueFn={r => `${r.count}×`} subFn={r => null}
              pairMode={true} />

            {/* Consistency */}
            </>
          }
          {batSubTab === 'consistency' && <>
            <SectionHeader title="Consistency" />
            <RecordTable emoji="📈" title="Most Innings Scoring 30+" rows={d.most_30_plus} stat="most_30_plus"
              valueFn={r => `${r.count}×`} subFn={r => `${r.runs} total runs`} />
            <RecordTable emoji="🎯" title="Most Innings Scoring 75-99" rows={d.most_75_to_99} stat="most_75_to_99"
              valueFn={r => `${r.count}×`} />
            <RecordTable emoji="✅" title="Most Matches with 20+ Score" rows={d.most_20_plus_matches} stat="most_20_plus_matches"
              valueFn={r => `${r.count}×`} />
            <RecordTable emoji="📅" title="Most Innings Played" rows={d.most_innings_played} stat="most_innings_played"
              valueFn={r => `${r.innings} inn`} subFn={r => `${r.runs} runs`} />

            <SectionHeader title="Streak Records" />
            <RecordTable emoji="🔥" title="Most Consecutive Innings 20+" rows={d.consecutive_20} stat="consecutive_20"
              valueFn={r => `${r.streak} inn`} subFn={r => `${fmtDate(r.from_date)} → ${fmtDate(r.to_date)}`} />
            <RecordTable emoji="💪" title="Most Consecutive Innings 30+" rows={d.consecutive_30} stat="consecutive_30"
              valueFn={r => `${r.streak} inn`} subFn={r => `${fmtDate(r.from_date)} → ${fmtDate(r.to_date)}`} />
            <RecordTable emoji="⭐" title="Most Consecutive Innings 50+" rows={d.consecutive_50} stat="consecutive_50"
              valueFn={r => `${r.streak} inn`} subFn={r => `${fmtDate(r.from_date)} → ${fmtDate(r.to_date)}`} />
            <RecordTable emoji="🛡️" title="Most Consecutive Innings Without a Duck" rows={d.consecutive_no_duck} stat="consecutive_no_duck"
              valueFn={r => `${r.streak} inn`} subFn={r => `${fmtDate(r.from_date)} → ${fmtDate(r.to_date)}`} />
            <RecordTable emoji="⭐" title="Most Consecutive Not Out Innings" rows={d.consecutive_not_out} stat="consecutive_not_out"
              valueFn={r => `${r.streak} inn`} subFn={r => `${fmtDate(r.from_date)} → ${fmtDate(r.to_date)}`} />

            {/* Phase */}
            </>
          }
          {batSubTab === 'situations' && <>
            <SectionHeader title="Situational Records" />
            <RecordTable emoji="🏃" title="Most Runs While Chasing" rows={d.most_runs_chasing} stat="most_runs_chasing"
              valueFn={r => `${r.runs}r`} subFn={r => `${r.innings} inn · avg ${r.average ?? '—'}`} />
            <RecordTable emoji="🏏" title="Most Runs Batting First" rows={d.most_runs_batting_first} stat="most_runs_batting_first"
              valueFn={r => `${r.runs}r`} subFn={r => `${r.innings} inn · avg ${r.average ?? '—'}`} />
            <RecordTable emoji="🏆" title="Most Runs in Winning Cause" rows={d.most_runs_winning} stat="most_runs_winning"
              valueFn={r => `${r.runs}r`} subFn={r => `${r.innings} inn · avg ${r.average ?? '—'}`} />
            <RecordTable emoji="💔" title="Most Runs in Losing Cause" rows={d.most_runs_losing} stat="most_runs_losing"
              valueFn={r => `${r.runs}r`} subFn={r => `${r.innings} inn · avg ${r.average ?? '—'}`} />
            <RecordTable emoji="📐" title="Best Average While Chasing (min 5 dis)" rows={d.best_avg_chasing} stat="best_avg_chasing"
              valueFn={r => `avg ${r.average ?? '∞'}`} subFn={r => `${r.runs}r · ${r.innings} inn`} />
            <RecordTable emoji="📐" title="Best Average Batting First (min 5 dis)" rows={d.best_avg_batting_first} stat="best_avg_batting_first"
              valueFn={r => `avg ${r.average ?? '∞'}`} subFn={r => `${r.runs}r · ${r.innings} inn`} />
            <RecordTable emoji="📐" title="Best Average in Wins (min 5 dis)" rows={d.best_avg_wins} stat="best_avg_wins"
              valueFn={r => `avg ${r.average ?? '∞'}`} subFn={r => `${r.runs}r · ${r.innings} inn`} />
            <RecordTable emoji="⚡" title="Highest Score While Chasing" rows={d.highest_score_chasing} stat="highest_score_chasing"
              valueFn={r => `${r.runs}${r.dismissal==='not_out'?'*':''}`} subFn={r => `${r.balls}b · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="⚡" title="Highest Score Batting First" rows={d.highest_score_batting_first} stat="highest_score_batting_first"
              valueFn={r => `${r.runs}${r.dismissal==='not_out'?'*':''}`} subFn={r => `${r.balls}b · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="👑" title="Most Times 30+ in Winning Team" rows={d.most_wins_with_30} stat="most_wins_with_30"
              valueFn={r => `${r.count}×`} subFn={r => `${r.runs} total runs`} />
            <RecordTable emoji="🛡️" title="Most Not Outs Batting First" rows={d.most_not_out_batting_first} stat="most_not_out_batting_first"
              valueFn={r => `${r.not_outs}×`} subFn={r => `${r.runs} runs`} />
            <RecordTable emoji="🏃" title="Most Not Outs While Chasing" rows={d.most_not_out_chasing} stat="most_not_out_chasing"
              valueFn={r => `${r.not_outs}×`} subFn={r => `${r.runs} runs`} />
          </>
          }

          {batSubTab === 'phase' && <>
            <SectionHeader title="Phase-Based Records" />
            <RecordTable emoji="🚀" title="Best Powerplay Average (min 20 balls)" rows={d.best_pp_average} stat="best_pp_average"
              valueFn={r => `avg ${r.average ?? '∞'}`} subFn={r => `${r.runs}r · ${r.innings} inn · ${r.dismissals} dis · PP`} />
            <RecordTable emoji="⚡" title="Best Powerplay SR (min 20 balls)" rows={d.best_pp_sr} stat="best_pp_sr"
              valueFn={r => `SR ${r.sr}`} subFn={r => `${r.runs}r · ${r.balls}b · PP`} />
            <RecordTable emoji="🎯" title="Best Middle Overs Average (min 20 balls)" rows={d.best_mid_average} stat="best_mid_average"
              valueFn={r => `avg ${r.average ?? '∞'}`} subFn={r => `${r.runs}r · ${r.innings} inn · ${r.dismissals} dis · middle`} />
            <RecordTable emoji="💀" title="Best Death Overs Average (min 10 balls)" rows={d.best_death_average} stat="best_death_average"
              valueFn={r => `avg ${r.average ?? '∞'}`} subFn={r => `${r.runs}r · ${r.innings} inn · ${r.dismissals} dis · death`} />
            <RecordTable emoji="🔥" title="Best Death Overs SR (min 10 balls)" rows={d.best_death_sr} stat="best_death_sr"
              valueFn={r => `SR ${r.sr}`} subFn={r => `${r.runs}r · ${r.balls}b · death`} />
            <RecordTable emoji="📊" title="Most Runs in Powerplay" rows={d.most_runs_pp} stat="most_runs_pp"
              valueFn={r => `${r.runs}r`} subFn={r => `${r.balls}b · PP`} />
            <RecordTable emoji="💥" title="Most Runs in Death Overs" rows={d.most_runs_death} stat="most_runs_death"
              valueFn={r => `${r.runs}r`} subFn={r => `${r.balls}b · death`} />

            {/* Overall */}
            <SectionHeader title="Overall" />
            <RecordTable emoji="👑" title="Most Times Top Scorer" rows={d.most_top_scorer} stat="most_top_scorer"
              valueFn={r => `${r.count}×`} />
            <RecordTable emoji="⏱️" title="Longest Innings (Balls Faced)" rows={d.longest_innings} stat="longest_innings"
              valueFn={r => `${r.balls}b`} subFn={r => `${r.runs}r · ${fmtDate(r.match_date)}`} />

          </>
          }

          </>
          }

          {/* ════════════════════════════════════════════════════════════ */}
          {/* 🎳 BOWLING TAB                                                 */}
          {/* ════════════════════════════════════════════════════════════ */}
          {tab === 'bowling' && <>

          {batSubTab === 'leaderboard' && <>
            <RecordTable emoji="🎳" title="Bowling Leaderboard" rows={d.leaderboard} stat="career_wickets"
              valueFn={r => `${r.wickets}w`}
              subFn={r => `${r.matches}m · Avg ${r.average ?? '—'} · Econ ${r.economy ?? '—'}`} />
            <RecordTable emoji="📐" title="Best Bowling Average (min 5 wkts)" rows={d.lowest_average} stat="lowest_average"
              valueFn={r => r.average} subFn={r => `${r.wickets}w · ${r.runs_conceded}r`} />
            <RecordTable emoji="💰" title="Best Economy (min 30 overs)" rows={d.lowest_economy} stat="lowest_economy"
              valueFn={r => r.economy} subFn={r => `${r.overs} ov · ${r.runs_conceded}r`} />
            <RecordTable emoji="⚡" title="Best Strike Rate (min 5 wkts)" rows={d.best_strike_rate} stat="best_strike_rate"
              valueFn={r => r.strike_rate} subFn={r => `${r.wickets}w`} />
            <RecordTable emoji="🧱" title="Most Maidens (Career)" rows={d.most_maidens_career} stat="most_maidens_career"
              valueFn={r => r.maidens} />
          </>}

          {batSubTab === 'innings' && <>
            <RecordTable emoji="🎯" title="Best Bowling Figures" rows={d.best_figures} stat="best_figures"
              valueFn={r => `${r.wickets}/${r.runs_conceded}`}
              subFn={r => `${r.overs} ov · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="5️⃣" title="Five-Wicket Hauls" rows={d.five_wkt_hauls} stat="five_wkt_hauls"
              valueFn={r => `${r.hauls}×`} />
            <RecordTable emoji="4️⃣" title="Four-Wicket Hauls" rows={d.four_wkt_hauls} stat="four_wkt_hauls"
              valueFn={r => `${r.hauls}×`} />
            <RecordTable emoji="🧱" title="Most Maidens in an Innings" rows={d.most_maidens_innings} stat="most_maidens_innings"
              valueFn={r => `${r.maidens}m`}
              subFn={r => `${r.overs} ov · ${r.wickets}w · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="•" title="Most Dot Balls in an Innings" rows={d.most_dots_innings} stat="most_dots_innings"
              valueFn={r => `${r.dots} dots`} subFn={r => fmtDate(r.match_date)} />
          </>}

          {batSubTab === 'career' && <>
            <RecordTable emoji="🏆" title="Career Wickets" rows={d.career_wickets} stat="career_wickets"
              valueFn={r => `${r.wickets}w`} subFn={r => `${r.matches}m`} />
            <RecordTable emoji="🧱" title="Career Maidens" rows={d.career_maidens} stat="career_maidens"
              valueFn={r => r.maidens} />
            <RecordTable emoji="🎳" title="Career Balls Bowled" rows={d.career_balls_bowled} stat="career_balls_bowled"
              valueFn={r => r.balls} subFn={r => `${r.overs} ov`} />
            <RecordTable emoji="3️⃣" title="Career 3-Wicket Hauls" rows={d.career_3_hauls} stat="career_3_hauls"
              valueFn={r => `${r.hauls}×`} />
            <RecordTable emoji="4️⃣" title="Career 4-Wicket Hauls" rows={d.career_4_hauls} stat="career_4_hauls"
              valueFn={r => `${r.hauls}×`} />
            <RecordTable emoji="5️⃣" title="Career 5-Wicket Hauls" rows={d.career_5_hauls} stat="career_5_hauls"
              valueFn={r => `${r.hauls}×`} />
          </>}

          {batSubTab === 'power' && <>
            <RecordTable emoji="💰" title="Best Match Economy (min 4 ov)" rows={d.best_match_economy} stat="best_match_economy"
              valueFn={r => r.economy}
              subFn={r => `${r.overs}-${r.wickets}w-${r.runs_conceded} · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="📊" title="Wickets per Match (min 5m)" rows={d.wkts_per_match} stat="wkts_per_match"
              valueFn={r => r.avg_wkts} subFn={r => `${r.wickets}w in ${r.matches}m`} />
            <RecordTable emoji="🚀" title="Most Powerplay Wickets" rows={d.most_pp_wkts} stat="most_pp_wkts"
              valueFn={r => `${r.wickets}w`} />
            <RecordTable emoji="•" title="Highest Dot-Ball % in Match" rows={d.highest_dot_pct} stat="highest_dot_pct"
              valueFn={r => `${r.dot_pct}%`}
              subFn={r => `${r.dots}/${r.balls} · ${fmtDate(r.match_date)}`} />
          </>}

          {batSubTab === 'partnerships' && <>
            <RecordTable emoji="🤝" title="Bowler + Catcher Combos" rows={d.bowler_catcher_pairs} stat="bowler_catcher_pairs"
              valueFn={r => `${r.dismissals}×`} pairMode={true} />
            <RecordTable emoji="🥅" title="Bowler + Keeper (Stumpings)" rows={d.bowler_keeper_stumpings} stat="bowler_keeper_stumpings"
              valueFn={r => `${r.dismissals}×`} pairMode={true} />
          </>}

          {batSubTab === 'consistency' && <>
            <RecordTable emoji="📅" title="Matches with a Wicket" rows={d.matches_with_wicket} stat="matches_with_wicket"
              valueFn={r => r.matches} />
            <RecordTable emoji="🔥" title="Consec. Matches with Wicket" rows={d.consec_matches_wkt} stat="consec_matches_wkt"
              valueFn={r => r.streak} />
            <RecordTable emoji="💰" title="Lowest Career Economy (min 10m)" rows={d.lowest_career_econ} stat="lowest_career_econ"
              valueFn={r => r.economy} subFn={r => `${r.matches}m`} />
          </>}

          {batSubTab === 'situations' && <>
            <RecordTable emoji="🛡️" title="Wickets Defending (2nd inn.)" rows={d.wkts_defending} stat="wkts_defending"
              valueFn={r => `${r.wickets}w`} />
            <RecordTable emoji="🎬" title="Wickets Bowling First" rows={d.wkts_bowling_first} stat="wkts_bowling_first"
              valueFn={r => `${r.wickets}w`} />
            <RecordTable emoji="🏆" title="Wickets in Wins" rows={d.wkts_in_wins} stat="wkts_in_wins"
              valueFn={r => `${r.wickets}w`} />
            <RecordTable emoji="📐" title="Average in Wins (min 5w)" rows={d.avg_in_wins} stat="avg_in_wins"
              valueFn={r => r.average} subFn={r => `${r.wickets}w · ${r.runs_conceded}r`} />
            <RecordTable emoji="💰" title="Economy in Wins (min 10 ov)" rows={d.econ_in_wins} stat="econ_in_wins"
              valueFn={r => r.economy} subFn={r => `${r.overs} ov · ${r.runs_conceded}r`} />
          </>}

          {batSubTab === 'phase' && <>
            <RecordTable emoji="🚀" title="Powerplay Wickets" rows={d.pp_wkts} stat="pp_wkts"
              valueFn={r => `${r.wickets}w`} />
            <RecordTable emoji="🏁" title="Death-Over Wickets (Last 5)" rows={d.death_wkts} stat="death_wkts"
              valueFn={r => `${r.wickets}w`} />
            <RecordTable emoji="💰" title="Best Powerplay Economy" rows={d.pp_economy} stat="pp_economy"
              valueFn={r => r.economy} subFn={r => `${r.legal_balls}b · ${r.runs_conceded}r`} />
            <RecordTable emoji="💰" title="Best Death-Over Economy" rows={d.death_economy} stat="death_economy"
              valueFn={r => r.economy} subFn={r => `${r.legal_balls}b · ${r.runs_conceded}r`} />
            <RecordTable emoji="💰" title="Best Middle-Over Economy" rows={d.mid_economy} stat="mid_economy"
              valueFn={r => r.economy} subFn={r => `${r.legal_balls}b · ${r.runs_conceded}r`} />
          </>}

          </>}

          {/* ════════════════════════════════════════════════════════════ */}
          {/* 🥅 WICKET-KEEPER TAB                                          */}
          {/* ════════════════════════════════════════════════════════════ */}
          {tab === 'keeper' && <>

          {batSubTab === 'leaderboard' && <>
            <RecordTable emoji="🥅" title="Keeper Leaderboard" rows={d.leaderboard} stat="career_dismissals"
              valueFn={r => `${r.dismissals}d`}
              subFn={r => `${r.caught_behind}c† · ${r.stumpings}st · ${r.matches_kept}m`} />
            <RecordTable emoji="🪤" title="Most Stumpings (Career)" rows={d.most_stumpings} stat="most_stumpings"
              valueFn={r => r.stumpings} />
            <RecordTable emoji="🧤" title="Most Caught-Behind (Career)" rows={d.most_caught_behind} stat="most_caught_behind"
              valueFn={r => r.caught_behind} />
            <RecordTable emoji="📅" title="Most Matches Kept" rows={d.most_matches_kept} stat="most_matches_kept"
              valueFn={r => r.matches_kept} />
          </>}

          {batSubTab === 'innings' && <>
            <RecordTable emoji="🎯" title="Most Dismissals in a Match" rows={d.most_dismissals_match} stat="most_dismissals_match"
              valueFn={r => `${r.dismissals}d`}
              subFn={r => `${r.caught_behind}c† · ${r.stumpings}st · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="🪤" title="Most Stumpings in a Match" rows={d.most_stumpings_match} stat="most_stumpings_match"
              valueFn={r => `${r.stumpings}st`} subFn={r => fmtDate(r.match_date)} />
            <RecordTable emoji="🧤" title="Most Caught-Behind in a Match" rows={d.most_cb_match} stat="most_cb_match"
              valueFn={r => `${r.caught_behind}c†`} subFn={r => fmtDate(r.match_date)} />
            <RecordTable emoji="🏃" title="Run-Outs as Keeper" rows={d.most_runouts_as_keeper} stat="most_runouts_as_keeper"
              valueFn={r => `${r.run_outs}ro`} subFn={r => fmtDate(r.match_date)} />
          </>}

          {batSubTab === 'career' && <>
            <RecordTable emoji="🏆" title="Career Dismissals" rows={d.career_dismissals} stat="career_dismissals"
              valueFn={r => `${r.dismissals}d`}
              subFn={r => `${r.caught_behind}c† · ${r.stumpings}st`} />
            <RecordTable emoji="🪤" title="Career Stumpings" rows={d.career_stumpings} stat="career_stumpings"
              valueFn={r => r.stumpings} />
            <RecordTable emoji="🧤" title="Career Caught-Behind" rows={d.career_cb} stat="career_cb"
              valueFn={r => r.caught_behind} />
            <RecordTable emoji="📅" title="Career Matches Kept" rows={d.career_matches_kept} stat="career_matches_kept"
              valueFn={r => r.matches_kept} />
          </>}

          {batSubTab === 'power' && <>
            <RecordTable emoji="📊" title="Dismissals per Match (min 5m)" rows={d.dismissals_per_match} stat="dismissals_per_match"
              valueFn={r => r.avg} subFn={r => `${r.dismissals}d in ${r.matches_kept}m`} />
            <RecordTable emoji="📊" title="Stumpings per Match (min 5m)" rows={d.stumpings_per_match} stat="stumpings_per_match"
              valueFn={r => r.avg} subFn={r => `${r.stumpings}st in ${r.matches_kept}m`} />
            <RecordTable emoji="🎯" title="Best Single-Match Performance" rows={d.best_single_match} stat="best_single_match"
              valueFn={r => `${r.dismissals}d`}
              subFn={r => `${r.caught_behind}c† · ${r.stumpings}st · ${fmtDate(r.match_date)}`} />
          </>}

          {batSubTab === 'partnerships' && <>
            <RecordTable emoji="🪤" title="Keeper + Bowler (Stumpings)" rows={d.keeper_bowler_stumpings} stat="keeper_bowler_stumpings"
              valueFn={r => `${r.dismissals}×`} pairMode={true} />
            <RecordTable emoji="🧤" title="Keeper + Bowler (C†)" rows={d.keeper_bowler_cb} stat="keeper_bowler_cb"
              valueFn={r => `${r.dismissals}×`} pairMode={true} />
          </>}

          {batSubTab === 'consistency' && <>
            <RecordTable emoji="🔥" title="Consec. Matches w/ Dismissal" rows={d.consec_matches_dismissal} stat="consec_matches_dismissal"
              valueFn={r => r.streak} />
            <RecordTable emoji="2️⃣" title="Matches with 2+ Dismissals" rows={d.matches_2plus} stat="matches_2plus"
              valueFn={r => r.matches} />
            <RecordTable emoji="📈" title="Consistency Ratio (min 5m)" rows={d.consistency_ratio} stat="consistency_ratio"
              valueFn={r => `${r.ratio}%`}
              subFn={r => `${r.with_dismissal}/${r.kept}m`} />
          </>}

          {batSubTab === 'situations' && <>
            <RecordTable emoji="🏆" title="Dismissals in Wins" rows={d.dismissals_in_wins} stat="dismissals_in_wins"
              valueFn={r => `${r.dismissals}d`} />
            <RecordTable emoji="🛡️" title="Dismissals Defending" rows={d.dismissals_defending} stat="dismissals_defending"
              valueFn={r => `${r.dismissals}d`} />
            <RecordTable emoji="🎬" title="Dismissals Bowling First" rows={d.dismissals_bowling_first} stat="dismissals_bowling_first"
              valueFn={r => `${r.dismissals}d`} />
            <RecordTable emoji="🎯" title="Best Match Situational" rows={d.best_match_situational} stat="best_match_situational"
              valueFn={r => `${r.dismissals}d`}
              subFn={r => `${r.caught_behind}c† · ${r.stumpings}st · ${fmtDate(r.match_date)}`} />
          </>}

          {batSubTab === 'phase' && <>
            <RecordTable emoji="🚀" title="Powerplay Dismissals" rows={d.pp_dismissals} stat="pp_dismissals"
              valueFn={r => `${r.dismissals}d`} />
            <RecordTable emoji="🏁" title="Death-Over Dismissals" rows={d.death_dismissals} stat="death_dismissals"
              valueFn={r => `${r.dismissals}d`} />
            <RecordTable emoji="🪤" title="Death-Over Stumpings" rows={d.death_stumper} stat="death_stumper"
              valueFn={r => `${r.stumpings}st`} />
          </>}

          </>}

          {/* ════════════════════════════════════════════════════════════ */}
          {/* 🧤 FIELDING (non-keeper) TAB                                  */}
          {/* ════════════════════════════════════════════════════════════ */}
          {tab === 'fielding' && <>

          {batSubTab === 'leaderboard' && <>
            <RecordTable emoji="🧤" title="Fielding Leaderboard" rows={d.leaderboard} stat="career_total_dismissals"
              valueFn={r => `${r.dismissals}d`}
              subFn={r => `${r.catches}c · ${r.run_outs}ro · ${r.matches}m`} />
            <RecordTable emoji="🏃" title="Career Run-Outs" rows={d.career_run_outs} stat="career_run_outs"
              valueFn={r => r.run_outs} />
            <RecordTable emoji="🏆" title="Career Total Dismissals" rows={d.career_total_dismissals} stat="career_total_dismissals"
              valueFn={r => r.dismissals} />
            <RecordTable emoji="📅" title="Matches with a Catch" rows={d.matches_with_catch} stat="matches_with_catch"
              valueFn={r => r.matches} />
          </>}

          {batSubTab === 'innings' && <>
            <RecordTable emoji="🎯" title="Most Catches in a Match" rows={d.most_catches_match} stat="most_catches_match"
              valueFn={r => `${r.catches}c`} subFn={r => fmtDate(r.match_date)} />
            <RecordTable emoji="🏃" title="Most Run-Outs in a Match" rows={d.most_runouts_match} stat="most_runouts_match"
              valueFn={r => `${r.run_outs}ro`} subFn={r => fmtDate(r.match_date)} />
            <RecordTable emoji="🏆" title="Most Total Dismissals in a Match" rows={d.most_total_dism_match} stat="most_total_dism_match"
              valueFn={r => `${r.dismissals}d`}
              subFn={r => `${r.catches}c · ${r.run_outs}ro · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="2️⃣" title="Matches with 2+ Catches" rows={d.matches_2plus} stat="matches_2plus_fld"
              valueFn={r => r.matches} />
          </>}

          {batSubTab === 'career' && <>
            <RecordTable emoji="🧤" title="Career Catches" rows={d.career_catches} stat="career_catches"
              valueFn={r => r.catches} />
            <RecordTable emoji="🏃" title="Career Run-Outs" rows={d.career_run_outs} stat="career_run_outs"
              valueFn={r => r.run_outs} />
            <RecordTable emoji="🏆" title="Career Total Dismissals" rows={d.career_total_dismissals} stat="career_total_dismissals"
              valueFn={r => r.dismissals} />
            <RecordTable emoji="📅" title="Matches Fielded" rows={d.matches_fielded} stat="matches_fielded"
              valueFn={r => r.matches} />
          </>}

          {batSubTab === 'power' && <>
            <RecordTable emoji="📊" title="Catches per Match (min 5m)" rows={d.catches_per_match} stat="catches_per_match"
              valueFn={r => r.avg} subFn={r => `${r.catches}c in ${r.matches}m`} />
            <RecordTable emoji="📊" title="Run-Outs per Match (min 5m)" rows={d.runouts_per_match} stat="runouts_per_match"
              valueFn={r => r.avg} subFn={r => `${r.run_outs}ro in ${r.matches}m`} />
            <RecordTable emoji="🎯" title="Best Single Performance" rows={d.best_single_perf} stat="best_single_perf"
              valueFn={r => `${r.dismissals}d`}
              subFn={r => `${r.catches}c · ${r.run_outs}ro · ${fmtDate(r.match_date)}`} />
          </>}

          {batSubTab === 'partnerships' && <>
            <RecordTable emoji="🤝" title="Catch + Bowler Combos" rows={d.catch_bowler_pairs} stat="catch_bowler_pairs"
              valueFn={r => `${r.catches}×`} pairMode={true} />
          </>}

          {batSubTab === 'consistency' && <>
            <RecordTable emoji="🔥" title="Consec. Matches with Catch" rows={d.consec_catch} stat="consec_catch"
              valueFn={r => r.streak} />
            <RecordTable emoji="🔥" title="Consec. Matches with Dismissal" rows={d.consec_any_dismissal} stat="consec_any_dismissal"
              valueFn={r => r.streak} />
            <RecordTable emoji="📈" title="Catch Ratio (min 10m)" rows={d.catch_ratio} stat="catch_ratio"
              valueFn={r => `${r.ratio}%`}
              subFn={r => `${r.catches}c in ${r.matches}m`} />
          </>}

          {batSubTab === 'situations' && <>
            <RecordTable emoji="🏆" title="Catches in Wins" rows={d.catches_in_wins} stat="catches_in_wins"
              valueFn={r => `${r.catches}c`} />
            <RecordTable emoji="🏃" title="Run-Outs in Wins" rows={d.runouts_in_wins} stat="runouts_in_wins"
              valueFn={r => `${r.run_outs}ro`} />
            <RecordTable emoji="🎯" title="Best Match Situational" rows={d.best_match_situational} stat="best_match_situational"
              valueFn={r => `${r.dismissals}d`}
              subFn={r => `${r.catches}c · ${r.run_outs}ro · ${fmtDate(r.match_date)}`} />
          </>}

          {batSubTab === 'phase' && <>
            <RecordTable emoji="🚀" title="Powerplay Catches" rows={d.pp_catches} stat="pp_catches"
              valueFn={r => `${r.catches}c`} />
            <RecordTable emoji="🏁" title="Death-Over Catches" rows={d.death_catches} stat="death_catches"
              valueFn={r => `${r.catches}c`} />
          </>}

          </>}

          {/* ════════════════════════════════════════════════════════════ */}
          {/* 🏆 OVERALL TAB                                                 */}
          {/* ════════════════════════════════════════════════════════════ */}
          {tab === 'overall' && <>

          {batSubTab === 'leaderboard' && <>
            <RecordTable emoji="🏆" title="Overall Leaderboard" rows={d.leaderboard} stat="most_points"
              valueFn={r => `${r.pom} POM`}
              subFn={r => `${r.matches}m · ${r.points} pts`} />
            <RecordTable emoji="🎖️" title="Most Player of the Match" rows={d.most_pom} stat="most_pom"
              valueFn={r => `${r.pom}×`} />
            <RecordTable emoji="⭐" title="Most Points (Fantasy)" rows={d.most_points} stat="most_points"
              valueFn={r => r.points} />
            <RecordTable emoji="📅" title="Most Appearances" rows={d.most_appearances} stat="most_appearances"
              valueFn={r => `${r.matches}m`} />
            <RecordTable emoji="👑" title="Captain Wins" rows={d.captain_wins} stat="captain_wins"
              valueFn={r => `${r.wins}w`} />
          </>}

          {batSubTab === 'innings' && <>
            <RecordTable emoji="🎯" title="Best All-Round Match" rows={d.best_all_round} stat="best_all_round"
              note="Score = runs + 20×wickets + 8×catches + 10×stumpings (keeper catches count as 8). This is a single-match all-round score — not UCC Points."
              valueFn={r => r.composite}
              subFn={r => `${r.runs}r · ${r.wickets}w · ${r.catches}c${r.stumpings > 0 ? ` · ${r.stumpings}st` : ''} · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="5️⃣" title="50 Runs + 3 Wickets (Career)" rows={d.fifty_and_three} stat="fifty_and_three"
              valueFn={r => `${r.count}×`} />
            <RecordTable emoji="💯" title="100 Runs + 3 Wickets (List)" rows={d.hundred_and_three} stat="hundred_and_three"
              valueFn={r => `${r.runs}r/${r.wickets}w`} subFn={r => fmtDate(r.match_date)} />
            <RecordTable emoji="🧤" title="50 Runs + 2 Catches" rows={d.fifty_and_two_catches} stat="fifty_and_two_catches"
              valueFn={r => `${r.count}×`} />
            <RecordTable emoji="5️⃣" title="5 Wkts + 30 Runs" rows={d.five_wkts_thirty_runs} stat="five_wkts_thirty_runs"
              valueFn={r => `${r.wickets}w/${r.runs}r`} subFn={r => fmtDate(r.match_date)} />
            <RecordTable emoji="🏆" title="Highest Composite Score (Match)" rows={d.best_composite} stat="best_composite"
              valueFn={r => r.composite}
              subFn={r => `${r.runs}r · ${r.wickets}w · ${fmtDate(r.match_date)}`} />
          </>}

          {batSubTab === 'career' && <>
            <RecordTable emoji="📅" title="Career Appearances" rows={d.career_appearances} stat="career_appearances"
              valueFn={r => `${r.matches}m`} />
            <RecordTable emoji="🎖️" title="Career POM Awards" rows={d.career_pom} stat="career_pom"
              valueFn={r => `${r.pom}×`} />
            <RecordTable emoji="⭐" title="Career Fantasy Points" rows={d.career_points} stat="career_points"
              valueFn={r => r.points} />
            <RecordTable emoji="🎯" title="All-Round Matches (30r+1w/c)" rows={d.career_all_round} stat="career_all_round"
              valueFn={r => `${r.count}×`} />
            <RecordTable emoji="🏆" title="Career Composite Score" rows={d.career_composite} stat="career_composite"
              valueFn={r => r.composite} />
          </>}

          {batSubTab === 'power' && <>
            <RecordTable emoji="💥" title="Boundaries + Wickets (Match)" rows={d.boundaries_plus_wkts} stat="boundaries_plus_wkts"
              valueFn={r => r.combined}
              subFn={r => `${r.boundaries}× 4s/6s · ${r.wickets}w · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="⚡" title="Impactful Matches (50r/3w/3d)" rows={d.impactful_matches} stat="impactful_matches"
              valueFn={r => `${r.count}×`} />
            <RecordTable emoji="🏆" title="Big Match Performances (50+ in Win)" rows={d.big_match_perfs} stat="big_match_perfs"
              valueFn={r => `${r.count}×`} />
          </>}

          {batSubTab === 'partnerships' && <>
            <RecordTable emoji="👑" title="Best Co-Captain Pairs" rows={d.captain_pairs} stat="captain_pairs"
              valueFn={r => `${r.matches}m`} pairMode={true} />
            <RecordTable emoji="🤝" title="Best All-Round Combos (Match)" rows={d.all_round_combo} stat="all_round_combo"
              valueFn={r => r.combined}
              subFn={r => `${r.runs}r · ${r.wickets}w · ${fmtDate(r.match_date)}`} pairMode={true} />
            <RecordTable emoji="🎯" title="Best Bowler-Catcher Pairs" rows={d.bowler_catcher_pairs} stat="bowler_catcher_pairs"
              valueFn={r => `${r.dismissals}×`} pairMode={true} />
          </>}

          {batSubTab === 'consistency' && <>
            <RecordTable emoji="🔥" title="Consec. Contributing Matches" rows={d.consec_contributing} stat="consec_contributing"
              valueFn={r => r.streak} />
            <RecordTable emoji="📆" title="Most Years Played" rows={d.most_years_played} stat="most_years_played"
              valueFn={r => `${r.years} yr`} />
            <RecordTable emoji="📅" title="Consec. Appearances" rows={d.consec_apps} stat="consec_apps"
              valueFn={r => r.streak} />
            <RecordTable emoji="🎯" title="30+ Runs and a Wicket" rows={d.matches_30_and_wicket} stat="matches_30_and_wicket"
              valueFn={r => `${r.count}×`} />
          </>}

          {batSubTab === 'situations' && <>
            <RecordTable emoji="🏃" title="Best in Chases" rows={d.best_in_chases} stat="best_in_chases"
              valueFn={r => r.score} subFn={r => `${r.runs}r · ${r.wickets}w · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="👑" title="Best as Captain" rows={d.best_as_captain} stat="best_as_captain"
              valueFn={r => r.score} subFn={r => `${r.runs}r · ${r.wickets}w · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="🏆" title="Best in Wins" rows={d.best_in_wins} stat="best_in_wins"
              valueFn={r => r.score} subFn={r => `${r.runs}r · ${r.wickets}w · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="🎯" title="Best in Pressure (Close)" rows={d.best_in_pressure} stat="best_in_pressure"
              valueFn={r => r.score} subFn={r => `${r.runs}r · ${r.wickets}w · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="🛡️" title="Best in Defense" rows={d.best_in_defense} stat="best_in_defense"
              valueFn={r => r.score} subFn={r => `${r.runs}r · ${r.wickets}w · ${fmtDate(r.match_date)}`} />
          </>}

          {batSubTab === 'phase' && <>
            <RecordTable emoji="🎬" title="Season Opener Performances" rows={d.season_opener} stat="season_opener"
              valueFn={r => r.score} subFn={r => `${r.runs}r · ${r.wickets}w · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="🏁" title="Season Finale Performances" rows={d.season_finale} stat="season_finale"
              valueFn={r => r.score} subFn={r => `${r.runs}r · ${r.wickets}w · ${fmtDate(r.match_date)}`} />
            <RecordTable emoji="📈" title="Year-over-Year Improvement" rows={d.yoy_improvement} stat="yoy_improvement"
              valueFn={r => r.improvement != null ? `+${r.improvement}%` : '—'}
              subFn={r => `${r.prev_year} → ${r.curr_year}: ${r.prev_score}→${r.score}`} />
          </>}

          </>}

        </div>
      )}

      {/* Drilldown Bottom Sheet */}
      {drilldown && (
        <div style={{ position:'fixed', inset:0, zIndex:50, display:'flex', flexDirection:'column', justifyContent:'flex-end' }}
          onClick={e => e.target===e.currentTarget && setDrilldown(null)}>
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.4)' }} onClick={() => setDrilldown(null)} />
          <div style={{ position:'relative', background:'white', borderRadius:'16px 16px 0 0',
            maxHeight:'80vh', display:'flex', flexDirection:'column', zIndex:51 }}>
            {/* Sticky header — handle + title + player(s) */}
            <div style={{ position:'sticky', top:0, background:'white', zIndex:2,
              borderRadius:'16px 16px 0 0', borderBottom:'1px solid #f1f5f9' }}>
              <div style={{ display:'flex', justifyContent:'center', padding:'10px 0 0' }}>
                <div style={{ width:36, height:4, borderRadius:2, background:'#e5e7eb' }} />
              </div>
              <div style={{ padding:'10px 16px 12px' }}>
                <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1rem', color:NAVY }}>
                  {ddData?.title || '…'}
                </div>
                <div style={{ fontSize:'0.75rem', color:'#9ca3af', marginTop:2 }}>
                  {drilldown.name2
                    ? `${drilldown.name} & ${drilldown.name2}`
                    : drilldown.name}
                  {' · '}{yearLabel(year)}
                </div>
              </div>
            </div>
            {/* Content */}
            <div style={{ overflowY:'auto', flex:1 }}>
              {ddLoading ? (
                <div style={{ textAlign:'center', padding:32, color:'#9ca3af' }}>Loading…</div>
              ) : !ddData?.rows?.length ? (
                <div style={{ textAlign:'center', padding:32, color:'#9ca3af' }}>No data</div>
              ) : (
                ddData.rows.map((r, i) => {
                  const cols = ddData?.columns || [];
                  // Drilldown type detection via columns set
                  const isBowlingSpell = cols.includes('W') && cols.includes('O') && cols.includes('Econ');
                  const isPhaseBowl    = cols.includes('W') && cols.includes('B') && !cols.includes('O');
                  const isPairBall     = cols.includes('Over') && (cols.includes('How') || cols.length <= 3);
                  const isKeeping      = cols.includes('Dism') && cols.includes('St');
                  const isFielding     = cols.includes('Dism') && cols.includes('C') && !cols.includes('St');
                  const isOverallMatch = cols.includes('Comp');
                  const isCaptainPair  = cols.includes('Team A');
                  const isAllRoundPair = cols.includes('P1 R');
                  const isBattingRuns  = !isBowlingSpell && !isPhaseBowl && !isPairBall && !isKeeping && !isFielding && !isOverallMatch && !isCaptainPair && !isAllRoundPair && r.runs !== undefined;

                  return (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 16px',
                    borderBottom:'1px solid #f9fafb', background: i===0?'#f8fafc':'white' }}>
                    {/* Rank */}
                    <div style={{ width:22, textAlign:'center', fontSize:'0.72rem',
                      color:'#9ca3af', fontWeight:600, flexShrink:0 }}>{i+1}</div>

                    {/* Date + sub-line */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'0.8rem', color:'#475569' }}>
                        {fmtDate(r.match_date)}
                        {r.innings !== undefined && r.innings !== null && (
                          <span style={{ marginLeft:6, fontSize:'0.68rem', color:'#94a3b8' }}>
                            Inn {r.innings}
                          </span>
                        )}
                        {r.over_number !== undefined && (
                          <span style={{ marginLeft:6, fontSize:'0.68rem', color:'#94a3b8' }}>
                            Ov {r.over_number}
                          </span>
                        )}
                      </div>
                      {/* Sub-line for bowling spell — overs/maidens */}
                      {isBowlingSpell && (
                        <div style={{ fontSize:'0.68rem', color:'#94a3b8', marginTop:1 }}>
                          {r.overs} ov{r.maidens > 0 ? ` · ${r.maidens}m` : ''}{r.economy !== undefined ? ` · econ ${r.economy}` : ''}
                        </div>
                      )}
                      {/* Phase bowling sub-line */}
                      {isPhaseBowl && (
                        <div style={{ fontSize:'0.68rem', color:'#94a3b8', marginTop:1 }}>
                          {r.balls}b · {r.runs}r
                        </div>
                      )}
                      {/* Overall composite sub-line */}
                      {isOverallMatch && (
                        <div style={{ fontSize:'0.68rem', color:'#94a3b8', marginTop:1 }}>
                          {r.runs}r{r.balls?`(${r.balls}b)`:''} · {r.wickets}w{r.overs?`/${r.overs}ov`:''}
                          {(r.catches||r.stumpings||r.run_outs) ? ` · ${(r.catches||0)+(r.stumpings||0)+(r.run_outs||0)}d` : ''}
                        </div>
                      )}
                      {/* Pair-ball details */}
                      {isPairBall && r.dismiss_type && (
                        <div style={{ fontSize:'0.68rem', color:'#94a3b8', marginTop:1 }}>
                          {r.dismiss_type.replace(/_/g,' ')}
                        </div>
                      )}
                      {/* Captain pair result */}
                      {isCaptainPair && r.capt1_team && (
                        <div style={{ fontSize:'0.68rem', color:'#94a3b8', marginTop:1 }}>
                          {r.capt1_team} vs {r.capt2_team}{r.result?` · ${r.result}`:''}
                        </div>
                      )}
                      {/* Batting dismissal */}
                      {isBattingRuns && r.dismissal && r.dismissal !== 'not_out' && (
                        <div style={{ fontSize:'0.68rem', color:'#94a3b8', marginTop:1 }}>
                          {r.dismissal.replace(/_/g,' ')}
                        </div>
                      )}
                    </div>

                    {/* ── Primary value column (right side) ── */}
                    {isBowlingSpell && (
                      <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.05rem',
                        color: r.wickets>=5?GRN:r.wickets>=3?AMB:NAVY, flexShrink:0 }}>
                        {r.wickets}/{r.runs}
                      </div>
                    )}
                    {isPhaseBowl && (
                      <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1rem',
                        color:NAVY, flexShrink:0 }}>{r.wickets}w</div>
                    )}
                    {isKeeping && (
                      <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1rem',
                        color:NAVY, flexShrink:0 }}>
                        {r.dismissals}d
                        <span style={{ fontWeight:400, fontSize:'0.72rem', color:'#9ca3af', marginLeft:4 }}>
                          ({r.caught_behind||0}c† · {r.stumpings||0}st{r.run_outs?` · ${r.run_outs}ro`:''})
                        </span>
                      </div>
                    )}
                    {isFielding && (
                      <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1rem',
                        color:NAVY, flexShrink:0 }}>
                        {r.dismissals}d
                        <span style={{ fontWeight:400, fontSize:'0.72rem', color:'#9ca3af', marginLeft:4 }}>
                          ({r.catches||0}c{r.run_outs?` · ${r.run_outs}ro`:''}{r.caught_behind?` · ${r.caught_behind}c†`:''})
                        </span>
                      </div>
                    )}
                    {isOverallMatch && (
                      <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.05rem',
                        color:GOLD, flexShrink:0 }}>{r.composite}</div>
                    )}
                    {isCaptainPair && (
                      <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1rem',
                        color:NAVY, flexShrink:0 }}>1m</div>
                    )}
                    {isAllRoundPair && (
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:1, flexShrink:0 }}>
                        <div style={{ fontSize:'0.72rem', color:'#475569' }}>P1: {r.p1_runs}r/{r.p1_wkts}w</div>
                        <div style={{ fontSize:'0.72rem', color:'#475569' }}>P2: {r.p2_runs}r/{r.p2_wkts}w</div>
                      </div>
                    )}
                    {isBattingRuns && (
                      <>
                        <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1rem',
                          color: r.runs>=50?GRN:r.runs>=25?AMB:NAVY, flexShrink:0 }}>
                          {r.runs}{r.dismissal==='not_out'?'*':''}
                          {r.balls ? <span style={{ fontWeight:400, fontSize:'0.75rem', color:'#9ca3af' }}> ({r.balls}b)</span> : null}
                        </div>
                        {r.fours !== undefined && (
                          <div style={{ fontSize:'0.7rem', color:'#9ca3af', flexShrink:0 }}>
                            {r.fours}×4 {r.sixes}×6
                          </div>
                        )}
                      </>
                    )}
                    {/* Phase batting / partnership-style (balls only, no runs) */}
                    {!isBowlingSpell && !isPhaseBowl && !isKeeping && !isFielding && !isOverallMatch && !isCaptainPair && !isAllRoundPair && !isBattingRuns && r.balls !== undefined && (
                      <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1rem',
                        color:NAVY, flexShrink:0 }}>{r.balls}b</div>
                    )}
                    {/* wicket number */}
                    {r.wicket !== undefined && (
                      <div style={{ fontSize:'0.7rem', color:'#9ca3af', flexShrink:0 }}>wkt {r.wicket}</div>
                    )}
                    {/* partnership-style p1/p2 names */}
                    {r.p1_name !== undefined && !isAllRoundPair && !isCaptainPair && (
                      <div style={{ fontSize:'0.68rem', color:'#64748b', flexShrink:0, maxWidth:120,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {r.p1_name} & {r.p2_name}
                      </div>
                    )}
                  </div>
                  );
                })
              )}
            </div>
            {/* Close */}
            <div style={{ padding:'12px 16px', borderTop:'1px solid #f1f5f9' }}>
              <button onClick={() => setDrilldown(null)} style={{ width:'100%', padding:10,
                borderRadius:10, border:'none', background:'#f1f5f9', color:'#475569',
                fontWeight:700, cursor:'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function HeadToHeadPageV2({ publicMode = false }) {
  const [allPlayers, setAllPlayers] = useState([]);
  const [p1, setP1] = useState(''), [p2, setP2] = useState('');
  const [year, setYear] = useState(2026);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [data, setData] = useState(null);
  const [spiderData, setSpiderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search1, setSearch1] = useState(''), [search2, setSearch2] = useState('');
  // Session 16 Pass 2 — scroll-collapse + filter modal (unified with other pages)
  const collapsed = useCollapseOnScroll(120);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    api.get(`${PUB()}/stats-v2/players?year=all&includeInactive=true`).then(r=>setAllPlayers(r.data.players||[]));
  }, []);

  const filtered = (s) => (includeInactive?allPlayers:allPlayers.filter(p=>p.is_active))
    .filter(p=>!s||p.display_name.toLowerCase().includes(s.toLowerCase()));

  const compare = () => {
    if (!p1||!p2||p1===p2) return;
    setLoading(true);
    Promise.all([
      api.get(`${PUB()}/stats-v2/h2h?p1=${p1}&p2=${p2}&year=${year}`),
      api.get(`${PUB()}/stats-v2/spider/${p1}?year=${year}&compare=${p2}`),
    ]).then(([h,sp])=>{
      setData(h.data); setSpiderData(sp.data);
      setFilterOpen(false);  // auto-close modal after compare
    }).catch(()=>{}).finally(()=>setLoading(false));
  };

  // Look up display names for collapsed bar
  const p1Name = allPlayers.find(p => p.id === p1)?.display_name || 'Player 1';
  const p2Name = allPlayers.find(p => p.id === p2)?.display_name || 'Player 2';
  const yearBadgeH2H = (!year || year === 'all') ? 'All Time'
    : String(year).includes(',') ? (String(year).split(',').length + ' Seasons')
    : String(year);

  const CompareRow = ({ label, v1, v2, higher='more' }) => {
    const n1=parseFloat(v1)||0, n2=parseFloat(v2)||0;
    const w=n1===n2?0:higher==='more'?(n1>n2?1:2):(n1<n2?1:2);
    return (
      <div style={{ display:'flex', alignItems:'center', padding:'8px 14px', borderBottom:'1px solid #f1f5f9' }}>
        <span style={{ flex:1, textAlign:'right', fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1rem', color:w===1?GRN:'#334155' }}>{v1??'—'}{w===1?' ✓':''}</span>
        <span style={{ width:110, textAlign:'center', fontSize:'0.68rem', color:'#94a3b8', fontWeight:600 }}>{label}</span>
        <span style={{ flex:1, textAlign:'left', fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1rem', color:w===2?GRN:'#334155' }}>{w===2?'✓ ':''}{v2??'—'}</span>
      </div>
    );
  };

  return (
    <div style={S.page}>
      <div style={{ position:'sticky', top:0, zIndex:50, boxShadow:'0 2px 8px rgba(0,0,0,0.08)' }}>
        {collapsed && data ? (
          /* COLLAPSED — compact bar */
          <div style={{ background:NAVY, display:'flex', alignItems:'center', gap:10, padding:'10px 14px', color:'white' }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.95rem', lineHeight:1.1 }}>
                ⚔️ Head to Head
              </div>
              <div style={{ fontSize:'0.7rem', color:'rgba(255,255,255,0.7)', marginTop:2,
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {p1Name} vs {p2Name} · {yearBadgeH2H}
              </div>
            </div>
            <button onClick={() => setFilterOpen(true)}
              style={{ background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.25)',
                borderRadius:14, padding:'5px 12px', color:'white',
                fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.72rem', cursor:'pointer' }}>
              ↕ Change
            </button>
          </div>
        ) : (
          /* EXPANDED — full form */
          <div style={{ background:NAVY, color:'white', padding:16 }}>
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.3rem', marginBottom:12 }}>⚔️ Head to Head</div>
            <div style={{ display:'flex', gap:8, marginBottom:8 }}>
              {[{s:search1,setS:setSearch1,p:p1,setP:setP1,f:filtered(search1),lbl:'Player 1'},
                {s:search2,setS:setSearch2,p:p2,setP:setP2,f:filtered(search2).filter(x=>x.id!==p1),lbl:'Player 2'}].map((cfg,i)=>(
                <div key={i} style={{ flex:1 }}>
                  <input placeholder={`Search ${cfg.lbl}…`} value={cfg.s} onChange={e=>cfg.setS(e.target.value)} style={{ ...S.select, width:'100%', marginBottom:4 }} />
                  <select style={{ ...S.select, width:'100%' }} value={cfg.p} onChange={e=>cfg.setP(e.target.value)}>
                    <option value="">— {cfg.lbl} —</option>
                    {cfg.f.map(p=><option key={p.id} value={p.id}>{p.display_name}{!p.is_active?' (inactive)':''}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:8, marginBottom:10 }}>
              <Controls year={year} setYear={setYear} includeInactive={includeInactive} setIncludeInactive={setIncludeInactive} showAll />
            </div>
            <button onClick={compare} disabled={!p1||!p2||p1===p2||loading}
              style={{ width:'100%', padding:10, borderRadius:8, border:'none', background:GOLD, color:NAVY, fontWeight:800, fontFamily:'Rajdhani,sans-serif', fontSize:'0.95rem', cursor:'pointer', opacity:(!p1||!p2||p1===p2)?0.5:1 }}>
              ⚔️ COMPARE
            </button>
          </div>
        )}
      </div>

      {/* Filter modal — reopens the full form */}
      <FilterSheet open={filterOpen} onClose={() => setFilterOpen(false)} title="Compare Players">
        <div style={{ display:'flex', gap:8, marginBottom:10 }}>
          {[{s:search1,setS:setSearch1,p:p1,setP:setP1,f:filtered(search1),lbl:'Player 1'},
            {s:search2,setS:setSearch2,p:p2,setP:setP2,f:filtered(search2).filter(x=>x.id!==p1),lbl:'Player 2'}].map((cfg,i)=>(
            <div key={i} style={{ flex:1 }}>
              <input placeholder={`Search ${cfg.lbl}…`} value={cfg.s} onChange={e=>cfg.setS(e.target.value)}
                style={{ width:'100%', marginBottom:4, padding:'8px 10px', border:'1px solid #d1d5db', borderRadius:8, fontSize:'0.85rem' }} />
              <select value={cfg.p} onChange={e=>cfg.setP(e.target.value)}
                style={{ width:'100%', padding:'8px 10px', border:'1px solid #d1d5db', borderRadius:8, fontSize:'0.85rem' }}>
                <option value="">— {cfg.lbl} —</option>
                {cfg.f.map(p=><option key={p.id} value={p.id}>{p.display_name}{!p.is_active?' (inactive)':''}</option>)}
              </select>
            </div>
          ))}
        </div>
        <Controls year={year} setYear={setYear} includeInactive={includeInactive} setIncludeInactive={setIncludeInactive} showAll />
        <button onClick={compare} disabled={!p1||!p2||p1===p2||loading}
          style={{ width:'100%', marginTop:14, padding:11, borderRadius:10, border:'none', background:GOLD, color:NAVY, fontWeight:800, fontFamily:'Rajdhani,sans-serif', fontSize:'0.95rem', cursor:'pointer', opacity:(!p1||!p2||p1===p2)?0.5:1 }}>
          ⚔️ COMPARE
        </button>
      </FilterSheet>

      {loading&&<Loading />}
      {data&&!loading&&(
        <div style={{ padding:'12px 16px' }}>
          <div style={{ display:'flex', alignItems:'center', padding:'10px 0 6px' }}>
            <Link to={`${publicMode ? "/club/stats" : "/stats-v2"}/${p1}`} style={{ flex:1, textAlign:'right', fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.05rem', color:NAVY, textDecoration:'none' }}>{data.player1?.display_name}</Link>
            <span style={{ width:110, textAlign:'center', fontSize:'0.7rem', color:'#94a3b8' }}>— vs —</span>
            <Link to={`${publicMode ? "/club/stats" : "/stats-v2"}/${p2}`} style={{ flex:1, textAlign:'left', fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.05rem', color:NAVY, textDecoration:'none' }}>{data.player2?.display_name}</Link>
          </div>
          {data.shared_matches>0&&<div style={{ textAlign:'center', fontSize:'0.7rem', color:'#94a3b8', marginBottom:10 }}>{data.shared_matches} matches together</div>}

          {spiderData?.players?.length>=2&&<div style={S.card}>
            <div style={S.hdr}>Performance Profile Comparison</div>
            <div style={{ display:'flex', justifyContent:'center', padding:'12px 0 4px' }}><RadarChart players={spiderData.players} size={220} /></div>
            <div style={{ display:'flex', justifyContent:'center', gap:16, padding:'0 14px 10px', fontSize:'0.68rem' }}>
              <span><span style={{ color:NAVY, fontWeight:800 }}>■</span> {data.player1?.display_name}</span>
              <span><span style={{ color:RED, fontWeight:800 }}>■</span> {data.player2?.display_name}</span>
            </div>
          </div>}

          <div style={S.card}>
            <div style={S.hdr}>🏏 Batting</div>
            <CompareRow label="Runs" v1={data.player1?.batting?.runs} v2={data.player2?.batting?.runs} />
            <CompareRow label="Average" v1={data.player1?.batting?.average} v2={data.player2?.batting?.average} />
            <CompareRow label="Strike Rate" v1={data.player1?.batting?.sr} v2={data.player2?.batting?.sr} />
            <CompareRow label="Highest" v1={data.player1?.batting?.highest} v2={data.player2?.batting?.highest} />
            <CompareRow label="50s" v1={data.player1?.batting?.fifties} v2={data.player2?.batting?.fifties} />
            <CompareRow label="100s" v1={data.player1?.batting?.centuries} v2={data.player2?.batting?.centuries} />
          </div>
          <div style={S.card}>
            <div style={S.hdr}>🎳 Bowling</div>
            <CompareRow label="Wickets" v1={data.player1?.bowling?.wickets} v2={data.player2?.bowling?.wickets} />
            <CompareRow label="Economy" v1={data.player1?.bowling?.economy} v2={data.player2?.bowling?.economy} higher="less" />
            <CompareRow label="Maidens" v1={data.player1?.bowling?.maidens} v2={data.player2?.bowling?.maidens} />
          </div>
          <div style={S.card}>
            <div style={S.hdr}>📈 Overall</div>
            <CompareRow label="Points" v1={Math.round(parseFloat(data.player1?.points?.points||0))} v2={Math.round(parseFloat(data.player2?.points?.points||0))} />
            <CompareRow label="Avg Pts" v1={data.player1?.points?.avg_pts} v2={data.player2?.points?.avg_pts} />
            <CompareRow label="Matches" v1={data.player1?.points?.matches} v2={data.player2?.points?.matches} />
            <CompareRow label="MOTM" v1={data.player1?.points?.motm} v2={data.player2?.points?.motm} />
          </div>

          {/* Direct Battles — always show if shared matches exist, uses ball_by_ball */}
          {data.shared_matches > 0 && <div style={S.card}>
            <div style={S.hdr}>
              <span>⚔️ Direct Battles</span>
              <span style={{ fontSize:'0.6rem', color:'#94a3b8', fontWeight:400 }}>ball-by-ball data</span>
            </div>
            <div style={{ padding:14 }}>
              {[
                { name: data.player1?.display_name, bowler: data.player2?.display_name, stat: data.versus?.p1_batting_vs_p2, bg:'#f0f9ff' },
                { name: data.player2?.display_name, bowler: data.player1?.display_name, stat: data.versus?.p2_batting_vs_p1, bg:'#f0fdf4' },
              ].map((s, i) => {
                const runs = parseInt(s.stat?.runs || 0);
                const balls = parseInt(s.stat?.balls || 0);
                const dismissed = parseInt(s.stat?.dismissals || 0);
                const boundaries = parseInt(s.stat?.boundaries || 0);
                const dots = parseInt(s.stat?.dots || 0);
                const sr = balls > 0 ? Math.round(runs / balls * 100) : 0;
                const dotPct = balls > 0 ? Math.round(dots / balls * 100) : 0;
                return (
                  <div key={i} style={{ padding:'12px 14px', background:s.bg, borderRadius:10, marginBottom:i===0?10:0 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                      <div>
                        <div style={{ fontWeight:700, fontSize:'0.82rem', color:NAVY }}>{s.name}</div>
                        <div style={{ fontSize:'0.65rem', color:'#94a3b8' }}>batting vs {s.bowler}</div>
                      </div>
                      {balls === 0 && <span style={{ fontSize:'0.72rem', color:'#94a3b8', fontStyle:'italic' }}>Never faced</span>}
                    </div>
                    {balls > 0 && <>
                      <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.4rem', color: runs>=20?GRN:runs>=10?AMB:'#334155', marginBottom:4 }}>
                        {runs} <span style={{ fontSize:'0.9rem', color:'#64748b' }}>({balls}b)</span>
                        <span style={{ fontSize:'0.9rem', color:'#64748b', marginLeft:8 }}>SR {sr}</span>
                      </div>
                      <div style={{ display:'flex', gap:16, fontSize:'0.72rem', color:'#64748b' }}>
                        <span>🏏 {boundaries} boundaries</span>
                        <span>⚫ {dotPct}% dots</span>
                        <span style={{ color: dismissed > 0 ? RED : GRN }}>
                          {dismissed > 0 ? `Out ${dismissed}×` : 'Never dismissed'}
                        </span>
                      </div>
                    </>}
                  </div>
                );
              })}
            </div>
          </div>}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// BANANA DRILLDOWN SHEET
// Shows the evidence behind a banana award for a specific player
// ═══════════════════════════════════════════════════════════════
function BananaDrilldownSheet({ memberId, memberName, awardTitle, stat, year, onClose, publicMode = false }) {
  const [drillData, setDrillData] = useState(null);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ stat, year: year || 2026 });
    api.get(`${PUB()}/stats-v2/drilldown/${memberId}?${params}`)
      .then(r => setDrillData(r.data))
      .catch(() => setDrillData({ title: 'Error loading data', columns: [], rows: [] }))
      .finally(() => setLoading(false));
  }, [memberId, stat, year]);

  const fmt = (val) => {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? '✅' : '—';
    if (typeof val === 'string' && val.length === 24 && val.includes('-')) return fmtDate(val);
    if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}/)) return fmtDate(val);
    return String(val);
  };

  // Map column header to row key
  const keyFor = (col, row) => {
    const map = {
      'date': 'match_date', 'runs': 'runs', 'balls': 'balls', '4s': 'fours',
      '6s': 'sixes', 'sr': 'strike_rate', 'how out': 'dismissal',
      'dismissed by': 'bowler_name', 'wickets': 'wickets', 'overs': 'overs',
      'economy': 'economy', 'wides': 'wides_count', 'nbs': 'noballs_count',
      'noballs': 'noballs_count', 'over': 'over_number', 'ball': 'ball_number',
      'commentary': 'commentary', 'points': 'total_pts', 'match result': 'winner_team',
      'type': 'extra_type', 'batsman': 'batsman', 'catches': 'catches',
      'stumpings': 'stumpings', 'run outs': 'run_outs', 'fielding contributions': 'fielding_contributions',
      'score': 'runs', 'position': 'batting_position', 'how out': 'dismissal_text',
      'times run out': 'times_run_out', 'run outs': 'times_run_out',
    };
    const k = map[col.toLowerCase()];
    if (k && row[k] !== undefined) return k;
    return Object.keys(row).find(rk => rk.toLowerCase().includes(col.toLowerCase().replace(/[^a-z]/g,''))) || Object.keys(row)[1] || Object.keys(row)[0];
  };

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:200, backdropFilter:'blur(2px)' }} />
      <div style={{ position:'fixed', bottom:'60px', left:0, right:0, background:'white', borderRadius:'16px 16px 0 0', zIndex:201, maxHeight:'calc(86vh - 60px)', display:'flex', flexDirection:'column', boxShadow:'0 -8px 40px rgba(0,0,0,0.25)' }}>
        {/* Handle bar */}
        <div style={{ padding:'10px 16px 0', flexShrink:0 }}>
          <div style={{ width:36, height:4, background:'#e5e7eb', borderRadius:2, margin:'0 auto 12px' }} />
          {/* Header with banana styling */}
          <div style={{ background:`linear-gradient(135deg,${NAVY},#2d4a8a)`, borderRadius:12, padding:'12px 14px', marginBottom:12, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div>
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1rem', color:GOLD }}>
                {memberName}
              </div>
              <div style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.6)', marginTop:2 }}>
                Evidence for: {awardTitle}
              </div>
              {!loading && drillData?.rows && (
                <div style={{ fontSize:'0.65rem', color:'rgba(255,255,255,0.4)', marginTop:2 }}>
                  {drillData.rows.length} record{drillData.rows.length !== 1 ? 's' : ''} · Year: {yearLabel(year)}
                </div>
              )}
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <Link to={`${publicMode ? "/club/stats" : "/stats-v2"}/${memberId}`} onClick={onClose}
                style={{ fontSize:'0.72rem', color:GOLD, textDecoration:'none', background:'rgba(255,255,255,0.1)', padding:'4px 10px', borderRadius:8, fontWeight:700 }}>
                Full Profile →
              </Link>
              <button onClick={onClose} style={{ border:'none', background:'rgba(255,255,255,0.15)', borderRadius:8, padding:'5px 10px', fontSize:'0.78rem', fontWeight:700, color:'white', cursor:'pointer' }}>✕</button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ overflowY:'auto', flex:1, padding:'0 0 80px' }}>
          {loading ? <Loading /> : !drillData || drillData.rows.length === 0 ? (
            <div style={{ padding:30, textAlign:'center', color:'#9ca3af' }}>
              <div style={{ fontSize:'2rem', marginBottom:8 }}>🎉</div>
              <div style={{ fontSize:'0.85rem' }}>No shame records found for {year === 'all' ? 'all time' : year}</div>
              <div style={{ fontSize:'0.72rem', color:'#cbd5e1', marginTop:4 }}>Try switching to "All Time"</div>
            </div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.79rem' }}>
                <thead>
                  <tr style={{ background:'#f8fafc', position:'sticky', top:0 }}>
                    {(drillData.columns||[]).map((col, i) => (
                      <th key={i} style={{ padding:'8px 10px', textAlign:i===0?'left':'center', color:'#475569', fontWeight:700, fontSize:'0.68rem', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap', borderBottom:'2px solid #e5e7eb' }}>
                        {col}
                      </th>
                    ))}
                    <th style={{ padding:'8px 6px', borderBottom:'2px solid #e5e7eb', width:28 }} />
                  </tr>
                </thead>
                <tbody>
                  {drillData.rows.map((row, ri) => {
                    const dateStr = fmtDate(row.match_date);
                    return (
                      <tr key={ri} style={{ borderBottom:'1px solid #f1f5f9', background: ri % 2 === 0 ? 'white' : '#fafafa' }}>
                        {(drillData.columns||[]).map((col, ci) => {
                          const key = keyFor(col, row);
                          let raw = row[key];
                          let display = fmt(raw);
                          let color = '#334155';
                          let fw = ci === 0 ? 500 : 700;
                          let ff = ci === 0 ? 'inherit' : 'Rajdhani,sans-serif';

                          if (col === 'Date') { display = dateStr; color = '#94a3b8'; ff = 'inherit'; fw = 400; }
                          if (col === 'Runs' || col === 'R') {
                            const v = parseInt(raw || 0);
                            color = v === 0 ? RED : v < 10 ? '#f97316' : v >= 20 ? GRN : AMB;
                          }
                          if (col === 'How Out') { display = (raw||'').replace(/_/g,' '); color = RED; }
                          if (col === 'Economy') { const v=parseFloat(raw||0); color = v>10?RED:v>8?'#f97316':v>6?AMB:GRN; }
                          if (col === 'SR') { const v=parseFloat(raw||0); color = v<60?RED:v<80?AMB:GRN; }
                          if (col === 'Points') { const v=parseFloat(raw||0); color = v<50?RED:v<100?AMB:GRN; }
                          if (col === 'Match Result') { color = (raw||'').toLowerCase().includes('ucc') || (raw||'').toLowerCase().includes('win') ? GRN : RED; }

                          return (
                            <td key={ci} style={{ padding:'8px 10px', textAlign:ci===0?'left':'center', fontFamily:ff, fontWeight:fw, color, whiteSpace:'nowrap', maxWidth: col==='Commentary'?200:undefined, overflow:'hidden', textOverflow:'ellipsis' }}>
                              {display}
                            </td>
                          );
                        })}
                        {/* Match detail link */}
                        <td style={{ padding:'6px', textAlign:'center' }}>
                          {row.match_id && (
                            <Link to={`/match/${row.match_id}`} onClick={onClose}
                              style={{ fontSize:'0.65rem', color:NAVY, textDecoration:'none', background:'#f0f9ff', padding:'2px 6px', borderRadius:5, fontWeight:700 }}>
                              🔍
                            </Link>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════
// BANANA AWARDS PAGE
// ═══════════════════════════════════════════════════════════════
export function BananaPageV2({ publicMode = false }) {
  const [data, setData]   = useState(null);
  const [year, setYear]   = useState(2026);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // { memberId, memberName, stat, awardTitle }
  // Session 16 Pass 2 — scroll-collapse + filter modal
  const collapsed = useCollapseOnScroll(120);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`${PUB()}/stats-v2/banana?year=${year}&includeInactive=${includeInactive}`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [year, includeInactive]);

  const br = i => i===0 ? '🥇' : i===1 ? '🥈' : i===2 ? '🥉' : `#${i+1}`;

  // Award Card — tapping the ROW opens drilldown, profile icon opens player profile
  const AC = ({ emoji, title, subtitle, rows, valueKey, valueSuffix='', tagline, drillStat, note }) => (
    <div style={{ ...S.card, marginBottom:14 }}>
      <div style={{ padding:'11px 14px', background:`linear-gradient(135deg,${NAVY},#2d4a8a)`, display:'flex', alignItems:'flex-start', gap:10 }}>
        <span style={{ fontSize:'1.4rem', marginTop:1 }}>{emoji}</span>
        <div style={{ flex:1 }}>
          <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'0.95rem', color:GOLD }}>{title}</div>
          <div style={{ fontSize:'0.68rem', color:'rgba(255,255,255,0.55)', marginTop:1 }}>{subtitle}</div>
          {tagline && <div style={{ fontSize:'0.64rem', color:'rgba(245,197,24,0.6)', marginTop:3, fontStyle:'italic' }}>{tagline}</div>}
          {note && <div style={{ fontSize:'0.62rem', color:'rgba(255,255,255,0.35)', marginTop:2 }}>ℹ {note}</div>}
        </div>
        {drillStat && <span style={{ fontSize:'0.58rem', color:'rgba(255,255,255,0.25)', alignSelf:'flex-end', marginTop:'auto' }}>tap row to verify</span>}
      </div>
      {(rows||[]).map((r,i) => {
        const val = r[valueKey];
        const displayVal = val !== null && val !== undefined ? `${val}${valueSuffix}` : '—';
        return (
          <div key={i}
            onClick={() => drillStat && setDrill({ memberId: r.id, memberName: r.display_name, stat: drillStat, awardTitle: title })}
            style={{ display:'flex', alignItems:'center', padding:'9px 14px', borderBottom:'1px solid #f1f5f9', cursor: drillStat ? 'pointer' : 'default' }}>
            <span style={{ width:28, flexShrink:0, textAlign:'center', fontSize:'0.95rem' }}>{br(i)}</span>
            <span style={{ flex:1, fontWeight:600, fontSize:'0.84rem', color:NAVY, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', paddingRight:8 }}>{r.display_name}</span>
            <span style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.95rem', color:RED, flexShrink:0, minWidth:60, textAlign:'right' }}>{displayVal}</span>
            <Link to={`${publicMode ? "/club/stats" : "/stats-v2"}/${r.id}`}
              onClick={e => e.stopPropagation()}
              style={{ marginLeft:8, fontSize:'0.72rem', color:'#94a3b8', textDecoration:'none', flexShrink:0 }}
              title="Full profile">👤</Link>
          </div>
        );
      })}
      {(!rows||rows.length===0) && !loading && (
        <div style={{ padding:12, textAlign:'center', color:'#9ca3af', fontSize:'0.75rem' }}>No victims this period 🎉</div>
      )}
    </div>
  );

  const Sec = ({ title }) => <div style={S.secLbl}>{title}</div>;

  const yearBadgeBV2 = (!year || year === 'all') ? 'All Time'
    : String(year).includes(',') ? (String(year).split(',').length + ' Seasons')
    : String(year);

  return (
    <div style={S.page}>
      <div style={{ position:'sticky', top:0, zIndex:50, boxShadow:'0 2px 8px rgba(0,0,0,0.08)' }}>
        {collapsed ? (
          /* COLLAPSED */
          <div style={{ background:`linear-gradient(135deg,${NAVY},#0f2340)`, color:'white',
            display:'flex', alignItems:'center', gap:10, padding:'10px 14px' }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.95rem', color:GOLD, lineHeight:1.1 }}>
                🍌 Banana Awards
              </div>
              <div style={{ fontSize:'0.7rem', color:'rgba(255,255,255,0.7)', marginTop:2,
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {yearBadgeBV2}
              </div>
            </div>
            <button onClick={() => setFilterOpen(true)}
              style={{ background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.25)',
                borderRadius:14, padding:'5px 12px', color:'white',
                fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.72rem', cursor:'pointer' }}>
              ↕ Change
            </button>
          </div>
        ) : (
          /* EXPANDED */
          <div style={{ background:`linear-gradient(135deg,${NAVY},#0f2340)`, color:'white', padding:16 }}>
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.5rem', marginBottom:2 }}>🍌 Banana Awards</div>
            <div style={{ fontSize:'0.7rem', color:'rgba(255,255,255,0.4)', marginBottom:12 }}>The stats nobody asked for — tap any row to see the evidence</div>
            <Controls year={year} setYear={setYear} includeInactive={includeInactive} setIncludeInactive={setIncludeInactive} showAll />
          </div>
        )}
      </div>
      <FilterSheet open={filterOpen} onClose={() => setFilterOpen(false)} title="Filters">
        <Controls year={year} setYear={setYear} includeInactive={includeInactive} setIncludeInactive={setIncludeInactive} showAll />
      </FilterSheet>
      {loading ? <Loading /> : (
        <div style={{ padding:'12px 16px' }}>

          <Sec title="🏏 Batting Shame" />

          <AC emoji="🦆" title="Duck Dynasty" subtitle="Most career ducks (0 runs, dismissed)"
            tagline='"At least they showed up"' drillStat="ducks"
            rows={data?.batting_shame?.duck_dynasty} valueKey="ducks" valueSuffix=" 🦆" />

          <AC emoji="🥇" title="Golden Duck Club" subtitle="Most first-ball dismissals (0 off ≤1 ball)"
            tagline='"In and out before the crease warmed up"' drillStat="golden_ducks"
            rows={data?.batting_shame?.golden_duck_club} valueKey="golden_ducks" />

          <AC emoji="🛒" title="Bargain Bin" subtitle="Lowest batting average (min 5 dismissals)"
            tagline='"Quality over quantity. Shame about quantity."' drillStat="runs"
            rows={data?.batting_shame?.bargain_bin} valueKey="average" valueSuffix=" avg" />

          <AC emoji="📉" title="Consistent Underachiever"
            subtitle="Highest % of innings scoring under 10 (min 10 innings)"
            tagline='"Not a slump. A personality trait."' drillStat="banana_low_scores"
            note="% of innings, not raw count — so playing more matches doesn't inflate it"
            rows={data?.batting_shame?.underachiever_pct} valueKey="pct_under10" valueSuffix="% innings" />

          <AC emoji="🪃" title="Boomerang Batter"
            subtitle="Most dismissals in the 40-49 or 85-99 range"
            tagline='"So close. Every. Single. Time."' drillStat="banana_boomerang"
            rows={data?.batting_shame?.boomerang_batter} valueKey="total_heartbreaks" valueSuffix=" heartbreaks" />

          <AC emoji="⏱️" title="Slowest Fifty" subtitle="Most balls needed to reach 50+"
            tagline='"They got there. We aged."' drillStat="banana_slowest_fifty"
            rows={data?.batting_shame?.slowest_fifty} valueKey="balls" valueSuffix="b" />

          <AC emoji="💀" title="Streak of Doom" subtitle="Longest consecutive duck streak"
            tagline='"Not a rough patch. A lifestyle."' drillStat="banana_consecutive_ducks"
            rows={data?.batting_shame?.streak_of_doom} valueKey="consecutive_ducks" valueSuffix=" in a row" />

          <Sec title="🎳 Bowling Shame" />

          <AC emoji="🚿" title="The Sprinkler" subtitle="Worst bowling economy (min 10 overs)"
            tagline='"More runs than a marathon route"' drillStat="banana_all_spells"
            rows={data?.bowling_shame?.the_sprinkler} valueKey="economy" valueSuffix=" econ" />

          <AC emoji="🤡" title="Wide-o-Matic" subtitle="Most career wides bowled"
            tagline='"The crease is over there. Just saying."' drillStat="banana_wides_detail"
            rows={data?.bowling_shame?.wideomatic} valueKey="wides" valueSuffix=" wides" />

          <AC emoji="📡" title="Signal Lost" subtitle="Most no-balls bowled"
            tagline='"Their foot has a mind of its own"' drillStat="noballs"
            rows={data?.bowling_shame?.signal_lost} valueKey="noballs" valueSuffix=" NBs" />

          <AC emoji="🕳️" title="The Invisible Man"
            subtitle="Highest % of bowling spells with zero wickets (min 5 spells)"
            tagline='"The wickets refused to notice"' drillStat="banana_wicketless"
            note="% not raw count — so bowling more doesn't inflate it"
            rows={data?.bowling_shame?.invisible_man} valueKey="wicketless_pct" valueSuffix="% wicketless" />

          <AC emoji="💥" title="The Grenade" subtitle="Most runs conceded in a single over"
            tagline='"One over. One crime scene."' drillStat="worst_over"
            rows={data?.bowling_shame?.the_grenade} valueKey="over_runs" valueSuffix=" in 1 over" />

          <AC emoji="4️⃣" title="Four Machine" subtitle="Most fours hit off their bowling"
            tagline='"The boundary was their best friend"' drillStat="banana_fours_conceded"
            rows={data?.bowling_shame?.most_fours_conceded} valueKey="fours_conceded" valueSuffix=" ×4" />

          <AC emoji="6️⃣" title="Six Appeal (Wrong Kind)" subtitle="Most sixes hit off their bowling"
            tagline='"Spectators loved them. For the wrong reason."' drillStat="banana_sixes_conceded"
            rows={data?.bowling_shame?.most_sixes_conceded} valueKey="sixes_conceded" valueSuffix=" ×6" />

          <Sec title="🧤 Fielding Shame" />

          <AC emoji="🧤" title="Butterfingers Club" subtitle="Most dropped catches (manually recorded)"
            tagline='"The ball had other plans"' drillStat="catches"
            rows={data?.fielding_shame?.butterfingers} valueKey="drops" valueSuffix=" drops" />

          <Sec title="🎭 All-Round Shame" />

          <AC emoji="🏆" title="Double Banana"
            subtitle="Most matches with duck + 0 wickets + 0 catches"
            tagline='"The complete package. Wrong kind."' drillStat="banana_double"
            rows={data?.allround_shame?.double_banana} valueKey="count" valueSuffix="×" />

          <AC emoji="🌟" title="Anti-MOM"
            subtitle="Worst UCC points in a single match"
            tagline='"A special kind of special"' drillStat="banana_worst_points"
            rows={data?.allround_shame?.anti_mom} valueKey="points" valueSuffix=" pts" />

          <AC emoji="🍌" title="Banana of the Season"
            subtitle="Worst total UCC points (min 3 matches)"
            tagline='"The award no one campaigned for"' drillStat="banana_all_points"
            rows={data?.allround_shame?.banana_of_season} valueKey="points" valueSuffix=" pts" />

          <AC emoji="👟" title="Slipper Shoe"
            subtitle="Most times run out as a batsman"
            tagline='"Called. Hesitated. Gone."' drillStat="banana_runout_batsman"
            rows={data?.allround_shame?.the_paradox} valueKey="times_run_out" valueSuffix=" run outs" />

          <AC emoji="😱" title="Panic Button"
            subtitle="Most times out for <10 runs batting at #6 or lower"
            tagline='"Lower order. Zero contribution. Every time."' drillStat="phase_death"
            rows={data?.allround_shame?.panic_button} valueKey="panic_dismissals" valueSuffix=" times" />

        </div>
      )}

      {drill && (
        <BananaDrilldownSheet
          memberId={drill.memberId}
          memberName={drill.memberName}
          awardTitle={drill.awardTitle}
          stat={drill.stat}
          year={year}
          onClose={() => setDrill(null)}
          publicMode={publicMode}
        />
      )}
    </div>
  );
}

export function MatchDetailPage({ publicMode = false }) {
  const { matchId } = useParams();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`${PUB()}/stats-v2/match/${matchId}`).then(r=>setData(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  }, [matchId]);

  if (loading) return <Loading />;
  if (!data) return <div style={{ padding:20, color:'#9ca3af', textAlign:'center' }}>Match not found</div>;

  const { result, batting=[], bowling=[], worm={}, wickets_per_over=[], key_moments=[] } = data;
  const bat1=batting.filter(r=>r.innings===1), bat2=batting.filter(r=>r.innings===2);
  const bowl1=bowling.filter(r=>r.innings===1), bowl2=bowling.filter(r=>r.innings===2);

  const BatCard = ({ rows, title }) => (
    <div style={S.card}>
      <div style={S.hdr}>{title}</div>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.78rem' }}>
          <thead><tr style={{ background:'#f8fafc' }}>
            {['Batsman','R','B','4s','6s','SR','How Out'].map(h=>(
              <th key={h} style={{ padding:'6px 8px', textAlign:h==='Batsman'?'left':'center', color:'#64748b', fontWeight:700, whiteSpace:'nowrap' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((r,i)=>(
              <tr key={i} style={{ borderTop:'1px solid #f1f5f9' }}>
                <td style={{ padding:'7px 8px', fontWeight:600, color:NAVY }}>
                  {r.member_id ? <Link to={`${publicMode ? "/club/stats" : "/stats-v2"}/${r.member_id}`} style={{ color:NAVY, textDecoration:'none' }}>{r.display_name||'—'}</Link> : (r.display_name||'—')}
                </td>
                <td style={{ textAlign:'center', fontFamily:'Rajdhani,sans-serif', fontWeight:800, color:parseInt(r.runs)>=50?GRN:parseInt(r.runs)>=20?AMB:'#334155' }}>{r.runs}{r.dismissal==='not_out'?'*':''}</td>
                <td style={{ textAlign:'center', color:'#64748b' }}>{r.balls}</td>
                <td style={{ textAlign:'center', color:'#64748b' }}>{r.fours}</td>
                <td style={{ textAlign:'center', color:'#64748b' }}>{r.sixes}</td>
                <td style={{ textAlign:'center', color:'#64748b' }}>{r.strike_rate}</td>
                <td style={{ textAlign:'center', color:'#94a3b8', fontSize:'0.7rem' }}>{r.dismissal_text||r.dismissal}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const BowlCard = ({ rows, title }) => (
    <div style={S.card}>
      <div style={S.hdr}>{title}</div>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.78rem' }}>
          <thead><tr style={{ background:'#f8fafc' }}>
            {['Bowler','O','M','R','W','Econ','Wd','NB'].map(h=>(
              <th key={h} style={{ padding:'6px 8px', textAlign:h==='Bowler'?'left':'center', color:'#64748b', fontWeight:700 }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((r,i)=>(
              <tr key={i} style={{ borderTop:'1px solid #f1f5f9' }}>
                <td style={{ padding:'7px 8px', fontWeight:600, color:NAVY }}>{r.display_name||'—'}</td>
                <td style={{ textAlign:'center', color:'#64748b' }}>{r.overs}</td>
                <td style={{ textAlign:'center', color:'#64748b' }}>{r.maidens}</td>
                <td style={{ textAlign:'center', color:'#64748b' }}>{r.runs}</td>
                <td style={{ textAlign:'center', fontFamily:'Rajdhani,sans-serif', fontWeight:800, color:parseInt(r.wickets)>=3?GRN:'#334155' }}>{r.wickets}</td>
                <td style={{ textAlign:'center', color:'#64748b' }}>{r.economy}</td>
                <td style={{ textAlign:'center', color:'#94a3b8' }}>{r.wides_count}</td>
                <td style={{ textAlign:'center', color:'#94a3b8' }}>{r.noballs_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <div style={{ background:NAVY, color:'white', padding:16 }}>
        <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.2rem', color:GOLD }}>Match Detail</div>
        {result&&<div style={{ fontSize:'0.8rem', color:'rgba(255,255,255,0.6)', marginTop:4 }}>
          {fmtDate(result.match_date)}{result.winner_team?` · Winner: ${result.winner_team}`:''}
        </div>}
      </div>
      <div style={{ padding:'12px 16px' }}>
        {(worm.inn1||worm.inn2)&&<div style={S.card}>
          <div style={S.hdr}>Match Worm</div>
          <WormChart worm={worm} wicketsPerOver={wickets_per_over} />
        </div>}
        {key_moments.length>0&&<div style={S.card}>
          <div style={S.hdr}>⚡ Key Moments</div>
          {key_moments.slice(0,15).map((m,i)=>(
            <div key={i} style={{ ...S.row, alignItems:'flex-start' }}>
              <span style={{ fontSize:'0.7rem', color:'#94a3b8', minWidth:50 }}>Ov {m.over_number}.{m.ball_number}</span>
              <span style={{ flex:1, fontSize:'0.8rem', color:m.is_wicket?RED:'#334155' }}>
                {m.is_wicket?'🎯 WICKET: ':'🏏 '}{m.batsman}{m.is_wicket?` out (${m.dismiss_type})`:`  hit ${m.runs}`}
              </span>
            </div>
          ))}
        </div>}
        {bat1.length>0&&<BatCard rows={bat1} title="🏏 Innings 1 — Batting" />}
        {bowl1.length>0&&<BowlCard rows={bowl1} title="🎳 Innings 1 — Bowling" />}
        {bat2.length>0&&<BatCard rows={bat2} title="🏏 Innings 2 — Batting" />}
        {bowl2.length>0&&<BowlCard rows={bowl2} title="🎳 Innings 2 — Bowling" />}
      </div>
    </div>
  );
}

export default PlayerProfilePage;
